/*
 * Static Indian statutory & regulatory content for the landing page.
 * Content is intentionally hand-written — no external API calls.
 * All figures reflect FY 2024-25 / AY 2025-26 unless stated otherwise.
 * Last curated: August 2025.
 */

export const LAST_UPDATED = "August 2025";

// Category → sub-items definition (drives the sidebar).
export const CATEGORIES = [
  {
    key: "income-tax",
    label: "Income Tax",
    authority: "CBDT",
    items: [
      "Tax Slabs & Rates",
      "TDS & TCS",
      "Capital Gains",
      "Chapter VI-A Deductions",
      "Presumptive Taxation",
      "Assessment & Appeals",
    ],
  },
  {
    key: "gst",
    label: "GST",
    authority: "CBIC",
    items: [
      "Registration",
      "Returns & Filing",
      "Input Tax Credit",
      "E-Way Bill",
      "Composition Scheme",
      "GST Audit",
    ],
  },
  {
    key: "company-sebi",
    label: "Company & SEBI Laws",
    authority: "MCA / SEBI",
    items: [
      "Companies Act 2013",
      "ROC Filings",
      "Board Meetings",
      "SEBI Regulations",
      "Insider Trading",
      "Listing Obligations",
    ],
  },
  {
    key: "fema-fcra",
    label: "FEMA & FCRA",
    authority: "RBI / MHA",
    items: [
      "Foreign Direct Investment",
      "External Commercial Borrowings",
      "Overseas Direct Investment",
      "FCRA Registration",
      "Compounding",
    ],
  },
  {
    key: "international-tax",
    label: "International Tax",
    authority: "CBDT",
    items: [
      "DTAAs & Tax Treaties",
      "POEM",
      "Equalisation Levy",
      "CbCR",
      "BEPS Action Plans",
    ],
  },
  {
    key: "transfer-pricing",
    label: "Transfer Pricing",
    authority: "CBDT",
    items: [
      "Arm's Length Price",
      "FAR Analysis",
      "Advance Pricing Agreements",
      "Safe Harbour Rules",
      "TP Documentation",
      "BEPS Transfer Pricing",
    ],
  },
  {
    key: "labour-laws",
    label: "Labour Laws",
    authority: "Ministry of Labour",
    items: [
      "PF & ESI",
      "Gratuity",
      "Minimum Wages",
      "Contract Labour Act",
      "Shops & Establishments",
      "Maternity Benefit",
    ],
  },
];

// Helper — build stable content page ids used as sidebar → page keys.
export const pageKey = (categoryKey, subitem) => `${categoryKey}:${subitem}`;

// -------------------------------------------------------------------
// Content dictionary. Each page follows the same shape:
//   { hero, summary, checklist, tables[], forms[], portals[], authority }
// -------------------------------------------------------------------
export const CONTENT_PAGES = {
  // ============== INCOME TAX ==============
  "income-tax:Tax Slabs & Rates": {
    authority: "CBDT",
    hero: {
      title: "Tax Slabs & Rates — AY 2025-26",
      description:
        "Old vs New regime slab structure, surcharge and cess for individuals, HUFs, firms, companies and co-operatives.",
    },
    summary: {
      governing: "Income-tax Act, 1961 · Finance Act, 2024",
      authority: "Central Board of Direct Taxes (CBDT)",
      effective: "Applicable to AY 2025-26 (income of FY 2024-25)",
      overview:
        "India offers two personal tax regimes. The New Regime (Section 115BAC) is the default and applies unless the taxpayer opts out — it has wider slabs and a lower headline rate but disallows most Chapter VI-A deductions. The Old Regime retains the traditional slabs but continues to allow HRA, LTA and 80C-80U style deductions. Firms, LLPs and domestic companies pay flat rates (with concessional rates for new manufacturing companies under Sections 115BAA / 115BAB). Every regime is topped by surcharge (income-linked) and a 4% Health & Education Cess on tax-plus-surcharge.",
    },
    tables: [
      {
        title: "New Regime — Individuals & HUFs (Sec. 115BAC, AY 2025-26)",
        columns: ["Income Slab (₹)", "Tax Rate"],
        rows: [
          ["Up to 3,00,000", "Nil"],
          ["3,00,001 – 7,00,000", "5%"],
          ["7,00,001 – 10,00,000", "10%"],
          ["10,00,001 – 12,00,000", "15%"],
          ["12,00,001 – 15,00,000", "20%"],
          ["Above 15,00,000", "30%"],
        ],
      },
      {
        title: "Old Regime — Individuals below 60 (Resident)",
        columns: ["Income Slab (₹)", "Tax Rate"],
        rows: [
          ["Up to 2,50,000", "Nil"],
          ["2,50,001 – 5,00,000", "5%"],
          ["5,00,001 – 10,00,000", "20%"],
          ["Above 10,00,000", "30%"],
        ],
      },
      {
        title: "Firms / LLPs / Local Authorities",
        columns: ["Entity", "Tax Rate", "Surcharge"],
        rows: [
          ["Firm / LLP", "30% flat", "12% if income > ₹1 cr"],
          ["Local Authority", "30% flat", "12% if income > ₹1 cr"],
        ],
      },
      {
        title: "Domestic Companies",
        columns: ["Regime", "Rate", "Effective (incl. surcharge & cess)"],
        rows: [
          ["Sec. 115BAA (any domestic co.)", "22%", "≈ 25.17%"],
          ["Sec. 115BAB (new mfg. co.)", "15%", "≈ 17.16%"],
          ["Turnover ≤ ₹400 cr (FY 22-23)", "25%", "≈ 27.82%"],
          ["Other domestic companies", "30%", "≈ 33.38%"],
        ],
      },
      {
        title: "Surcharge — Individuals (Both regimes)",
        columns: ["Total Income", "Surcharge Rate"],
        rows: [
          ["> ₹50 lakh – ₹1 cr", "10%"],
          ["> ₹1 cr – ₹2 cr", "15%"],
          ["> ₹2 cr – ₹5 cr", "25% (Old) / 25% (New)"],
          ["> ₹5 cr", "37% (Old) / 25% cap (New Regime)"],
        ],
      },
    ],
    checklist: [
      "Confirm regime choice — New Regime is default from AY 2024-25.",
      "Salaried taxpayers can switch every year; business income can switch only once.",
      "Verify surcharge threshold — marginal relief available at each cap.",
      "Health & Education Cess of 4% applies on tax + surcharge.",
      "Rebate u/s 87A: ₹25,000 (New) / ₹12,500 (Old) for income up to threshold.",
      "Senior citizens: ₹3 lakh exemption limit (60-79) and ₹5 lakh (80+) under Old Regime.",
      "Companies opting for 115BAA/115BAB cannot claim additional depreciation, SEZ deductions, or 80-IA/IB benefits.",
      "File Form 10-IEA to opt out of New Regime for business/professional income.",
    ],
    forms: [
      "Form 10-IEA — Opt-out of New Regime (business income)",
      "ITR-1 to ITR-7 — Return of income",
      "Form 26AS / AIS / TIS — Prefilled income statements",
    ],
    portals: [
      { label: "Income Tax e-Filing Portal", url: "https://www.incometax.gov.in" },
      { label: "CBDT — incometaxindia.gov.in", url: "https://incometaxindia.gov.in" },
    ],
  },

  "income-tax:TDS & TCS": {
    authority: "CBDT",
    hero: {
      title: "TDS & TCS — Sections 192-196D and 206C",
      description:
        "Complete withholding rates, thresholds, deposit and return timelines for residents and non-residents.",
    },
    summary: {
      governing: "Chapter XVII, Income-tax Act, 1961",
      authority: "CBDT · TIN-NSDL (Protean) filing infra",
      effective: "FY 2024-25 rates; Finance Act 2024 amendments",
      overview:
        "TDS obliges the payer to deduct tax at source before making specified payments and deposit it with the government within due dates. Failure attracts interest (1%/1.5% p.m.) and disallowance of expenditure. Every deductor must obtain a TAN, file quarterly returns (24Q/26Q/27Q/27EQ), and issue certificates in Form 16/16A. TCS mirrors the concept for sellers of specified goods (Section 206C) and remittances under LRS.",
    },
    tables: [
      {
        title: "Major TDS Sections — Residents (FY 2024-25)",
        columns: ["Section", "Nature of Payment", "Threshold (₹)", "Rate"],
        rows: [
          ["192", "Salary", "Basic exemption", "Slab"],
          ["192A", "PF withdrawal < 5 yrs", "50,000", "10%"],
          ["193", "Interest on securities", "10,000", "10%"],
          ["194", "Dividend", "5,000", "10%"],
          ["194A", "Interest (non-bank)", "5,000 / 40,000 / 50,000*", "10%"],
          ["194C", "Contractor / sub-contractor", "30,000 single / 1,00,000 annual", "1% Ind/HUF, 2% others"],
          ["194H", "Commission / brokerage", "15,000", "5% (2% from Oct-24)"],
          ["194I", "Rent — plant / land / building", "2,40,000", "2% / 10%"],
          ["194J", "Professional / technical fees", "30,000", "10% (2% technical)"],
          ["194Q", "Purchase of goods", "50 lakh", "0.1%"],
          ["194R", "Business perquisites", "20,000", "10%"],
          ["194S", "Virtual Digital Assets", "10,000 / 50,000", "1%"],
        ],
      },
      {
        title: "TDS — Non-Residents (Sec. 195 / 196D)",
        columns: ["Payment", "Rate + Surcharge + Cess"],
        rows: [
          ["Interest to NR", "20% (or treaty rate if lower)"],
          ["Royalty / FTS", "20% (LTL 10% for approved cases)"],
          ["LTCG on shares/debentures", "10% / 12.5%"],
          ["STCG on listed securities", "15% / 20%"],
          ["FPI income (Sec. 196D)", "20%"],
        ],
      },
      {
        title: "TDS — Deposit & Return Timelines",
        columns: ["Item", "Frequency", "Due Date", "Authority"],
        rows: [
          ["TDS deposit", "Monthly", "7th of following month (April deducted → 30 Apr)", "TIN-NSDL"],
          ["TDS return — 24Q/26Q/27Q", "Quarterly", "31 Jul, 31 Oct, 31 Jan, 31 May", "TRACES"],
          ["Form 16 (Salary)", "Annual", "15 June", "Deductor"],
          ["Form 16A (Non-salary)", "Quarterly", "15 days from return due", "TRACES"],
          ["TCS return — 27EQ", "Quarterly", "15 Jul, 15 Oct, 15 Jan, 15 May", "TRACES"],
        ],
      },
      {
        title: "TCS — Section 206C (FY 2024-25)",
        columns: ["Item", "Rate", "Threshold"],
        rows: [
          ["Sale of goods (206C(1H))", "0.1%", "> ₹50 lakh"],
          ["Overseas Tour Programme", "5% / 20%", "First ₹7 lakh / above"],
          ["LRS Remittance", "5% / 20%", "First ₹7 lakh / above"],
          ["Scrap", "1%", "—"],
          ["Timber / forest produce", "2.5%", "—"],
          ["Motor vehicle > ₹10 lakh", "1%", "—"],
        ],
      },
    ],
    checklist: [
      "Obtain TAN before making any deductible payment.",
      "Deposit TDS by 7th of following month (30 Apr for March).",
      "Higher TDS (twice normal rate) for non-filers under Section 206AB.",
      "20% TDS if PAN not furnished (Section 206AA).",
      "Issue Form 16 by 15 June; Form 16A within 15 days of quarterly return.",
      "File 24Q/26Q/27Q/27EQ every quarter — late fee ₹200/day u/s 234E.",
      "Reconcile Form 26AS / AIS quarterly to prevent mismatch.",
      "Track lower/nil deduction certificates u/s 197 issued to deductees.",
    ],
    forms: [
      "Form 16 — Salary TDS certificate",
      "Form 16A — Non-salary TDS certificate",
      "Form 27D — TCS certificate",
      "Form 24Q / 26Q / 27Q / 27EQ — Quarterly returns",
      "Form 13 — Lower/Nil deduction application",
    ],
    portals: [
      { label: "TRACES — TDS Reconciliation", url: "https://www.tdscpc.gov.in" },
      { label: "Protean (NSDL) — Challan & Return upload", url: "https://www.protean-tinpan.com" },
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "income-tax:Capital Gains": {
    authority: "CBDT",
    hero: {
      title: "Capital Gains — Sections 45 to 55A",
      description:
        "Short-term vs long-term classification, tax rates, indexation and rollover exemptions.",
    },
    summary: {
      governing: "Chapter IV-E, Income-tax Act, 1961",
      authority: "CBDT · Finance (No. 2) Act, 2024 amendments",
      effective: "Revised rates effective 23 July 2024",
      overview:
        "Capital gains arise when a capital asset is transferred at a value higher than its cost of acquisition. Assets are classified as short-term or long-term based on holding period, which drives the applicable tax rate. The Finance (No. 2) Act 2024 rationalised the regime — LTCG is now 12.5% across most assets without indexation (immovable property retains an option). Exemptions under Sections 54, 54B, 54EC, 54F remain the primary rollover reliefs for taxpayers reinvesting in specified assets.",
    },
    tables: [
      {
        title: "Holding Period Thresholds (post 23-Jul-2024)",
        columns: ["Asset", "Short-Term if held ≤", "Long-Term if held >"],
        rows: [
          ["Listed equity shares & equity MFs", "12 months", "12 months"],
          ["Unlisted shares", "24 months", "24 months"],
          ["Immovable property", "24 months", "24 months"],
          ["Debt MFs (post 01-Apr-23)", "Always STCG (slab)", "—"],
          ["Gold / other assets", "24 months", "24 months"],
        ],
      },
      {
        title: "Tax Rates (Transfer on/after 23 Jul 2024)",
        columns: ["Asset", "STCG", "LTCG"],
        rows: [
          ["Listed equity / equity MF (STT paid)", "20%", "12.5% (₹1.25 L exempt)"],
          ["Unlisted shares", "Slab", "12.5% (no indexation)"],
          ["Immovable property", "Slab", "12.5% w/o indexation OR 20% with indexation (opt)"],
          ["Debt MFs (post Apr-23)", "Slab", "Slab"],
          ["Gold / other assets", "Slab", "12.5% (no indexation)"],
        ],
      },
      {
        title: "Rollover Exemptions",
        columns: ["Section", "Eligible Asset Sold", "Reinvestment", "Lock-in"],
        rows: [
          ["54", "Residential house", "Another residential house", "3 yrs"],
          ["54B", "Agricultural land", "New agri land", "3 yrs"],
          ["54EC", "Any LT capital asset", "NHAI / REC bonds (₹50 L cap)", "5 yrs"],
          ["54F", "Any LT asset (non-house)", "One residential house", "3 yrs"],
        ],
      },
    ],
    checklist: [
      "Identify transaction date — pre vs post 23 Jul 2024 changes classification & rate.",
      "Compute indexed cost via CII where indexation option retained (immovable property).",
      "Set-off STCL against STCG/LTCG; LTCL only against LTCG (carry 8 years).",
      "Section 54 requires purchase within 1 year before or 2 years after sale; construction within 3 years.",
      "Capital Gains Account Scheme (CGAS) if reinvestment not made before ITR due date.",
      "Section 54EC bonds — invest within 6 months, ₹50 lakh annual cap.",
      "Report every transfer in Schedule CG of ITR-2/3 with cost, sale, and expenses.",
      "STT payment mandatory for equity LTCG concessional rate.",
    ],
    forms: [
      "Schedule CG (ITR-2 / ITR-3)",
      "Form 26QB — TDS on immovable property sale (1%)",
      "CGAS deposit — via authorised banks",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
      { label: "CII notifications", url: "https://incometaxindia.gov.in" },
    ],
  },

  "income-tax:Chapter VI-A Deductions": {
    authority: "CBDT",
    hero: {
      title: "Chapter VI-A Deductions — Sections 80C to 80U",
      description:
        "Comprehensive deductions available under the Old Regime — investments, insurance, donations and disability reliefs.",
    },
    summary: {
      governing: "Chapter VI-A, Income-tax Act, 1961",
      authority: "CBDT",
      effective: "Available to taxpayers opting for Old Regime (AY 2025-26)",
      overview:
        "Chapter VI-A allows taxpayers under the Old Regime to reduce gross total income by claiming deductions for defined savings, insurance, healthcare, home loan interest, donations and disabilities. Most deductions are unavailable under the New Regime (Sec. 115BAC) except 80CCD(2) (employer NPS contribution), 80CCH (Agniveer Corpus Fund) and 80JJAA (new employee cost). Proofs must be preserved for at least 6 years for scrutiny.",
    },
    tables: [
      {
        title: "Section 80C Family (₹1.5 lakh combined cap)",
        columns: ["Section", "Deduction", "Cap"],
        rows: [
          ["80C", "PPF, EPF, ELSS, LIC, PPF, principal on home loan, tuition fees, NSC, SSY", "₹1,50,000"],
          ["80CCC", "Pension fund contribution", "Within 80C cap"],
          ["80CCD(1)", "Employee NPS contribution", "10% salary within 80C"],
          ["80CCD(1B)", "Additional NPS", "₹50,000"],
          ["80CCD(2)", "Employer NPS contribution", "14% Govt / 10% others (also in New Regime)"],
        ],
      },
      {
        title: "Health, Loans & Disabilities",
        columns: ["Section", "Deduction", "Limit (₹)"],
        rows: [
          ["80D", "Health insurance premium", "25k self / 50k senior; +25/50k parents"],
          ["80DD", "Maintenance of disabled dependent", "75k / 1.25 L (severe)"],
          ["80DDB", "Medical treatment (specified diseases)", "40k / 1 L (senior)"],
          ["80E", "Interest on education loan", "No cap (8 yrs)"],
          ["80EE / 80EEA", "Home loan interest (first-time buyer)", "50k / 1.5 L"],
          ["80EEB", "Interest on electric vehicle loan", "1,50,000"],
          ["80G", "Donations — approved trusts", "50%/100%; 10% GTI cap"],
          ["80GG", "Rent (no HRA)", "Lower of 5k p.m. / 25% GTI"],
          ["80TTA", "Savings a/c interest", "10,000"],
          ["80TTB", "Bank/post interest — senior citizens", "50,000"],
          ["80U", "Disability of self", "75k / 1.25 L"],
        ],
      },
    ],
    checklist: [
      "Verify PPF & EPF contribution proofs by 31 March.",
      "Health insurance must be paid via non-cash mode (except preventive checkup ≤ ₹5k).",
      "Section 80G donations require 80G certificate + Form 10BE (from FY 22-23).",
      "80TTA is only for savings accounts, not FDs.",
      "80EEA additional ₹1.5 L is over 80C — for loans sanctioned Apr-19 to Mar-22.",
      "Employer NPS deduction (80CCD(2)) is the sole Chapter VI-A relief in New Regime.",
      "80JJAA — 30% deduction on additional employee cost for 3 years (both regimes).",
      "Aggregate cap 100% of gross total income — deductions cannot create a loss.",
    ],
    forms: [
      "Form 12BB — Investment declaration to employer",
      "Form 10BE — Donation certificate (80G)",
      "Form 10-IA — Certificate for disability (80DD/80U)",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
      { label: "NPS — enps.nsdl.com", url: "https://enps.nsdl.com" },
    ],
  },

  "income-tax:Presumptive Taxation": {
    authority: "CBDT",
    hero: {
      title: "Presumptive Taxation — Sections 44AD, 44ADA, 44AE",
      description:
        "Simplified schemes for small businesses, professionals and transporters — deemed profits with no books requirement.",
    },
    summary: {
      governing: "Sections 44AD, 44ADA, 44AE, Income-tax Act, 1961",
      authority: "CBDT",
      effective: "Turnover limits enhanced by Finance Act 2023",
      overview:
        "Presumptive taxation lets eligible small taxpayers declare income at a prescribed percentage of turnover without maintaining detailed books or getting audited. Sec. 44AD covers eligible businesses (traders / retailers / SMEs) with turnover up to ₹2 crore (₹3 crore if ≤ 5% cash receipts). Sec. 44ADA applies to specified professionals up to ₹50 lakh (₹75 lakh with 5% cash cap). Sec. 44AE covers goods-carriage transporters. Taxpayers opting-out must maintain books and get audited for 5 subsequent years.",
    },
    tables: [
      {
        title: "Presumptive Regime Comparison (FY 2024-25)",
        columns: ["Section", "Eligible Taxpayer", "Turnover Cap", "Deemed Profit"],
        rows: [
          ["44AD", "Resident individual/HUF/firm (not LLP) in eligible business", "₹2 cr (₹3 cr if ≤ 5% cash)", "8% (6% if digital receipt)"],
          ["44ADA", "Specified profession (CA, CS, doctor, engineer, architect, IT)", "₹50 L (₹75 L if ≤ 5% cash)", "50%"],
          ["44AE", "Person owning ≤ 10 goods carriages", "No turnover cap", "₹1,000 p.m. per tonne (>12T); ₹7,500 p.m. otherwise"],
        ],
      },
      {
        title: "Restrictions & Exit Rules",
        columns: ["Aspect", "44AD", "44ADA"],
        rows: [
          ["Advance tax", "One installment by 15 Mar", "Same"],
          ["Books of account", "Not required", "Not required"],
          ["Tax audit", "Not required (declaring ≥ deemed)", "Same"],
          ["Ineligible", "LLP, agency biz, commission/brokerage", "Non-specified professions"],
          ["Opt-out impact", "Locked-out 5 yrs; books & audit needed", "Same"],
        ],
      },
    ],
    checklist: [
      "Verify eligibility — LLP and agency/brokerage businesses cannot claim 44AD.",
      "For 44AD, 6% presumption applies only where receipts are via banking channels before ITR due date.",
      "44ADA is limited to professions listed in Sec. 44AA(1) — CA/CS/CMA/doctor/engineer/architect/lawyer/IT/interior design.",
      "Advance tax lump-sum by 15 March; interest u/s 234C otherwise.",
      "Cannot claim further deductions under Sections 30-38 once presumptive is chosen.",
      "44AE has no turnover cap but limited to owners of ≤ 10 goods vehicles.",
      "File ITR-4 (Sugam) for presumptive returns.",
      "Once opted out, 5-year book-keeping & audit lock-in applies (Sec. 44AD(4)).",
    ],
    forms: [
      "ITR-4 (Sugam) — Presumptive income return",
      "Form 3CB / 3CD — Tax audit (if opted out)",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "income-tax:Assessment & Appeals": {
    authority: "CBDT",
    hero: {
      title: "Assessment & Appeals",
      description:
        "Types of assessment, scrutiny timelines, and the four-tier appellate ladder from CIT(A) to the Supreme Court.",
    },
    summary: {
      governing: "Chapters XIV & XX, Income-tax Act, 1961",
      authority: "Assessing Officer · CIT(A) / DRP · ITAT · High Court · SC",
      effective: "Faceless Assessment & Appeals from FY 2020-21 onwards",
      overview:
        "The assessment process determines the final tax liability after return filing. It ranges from summary assessment (Sec. 143(1)) to detailed scrutiny (143(3)), best-judgment assessment (144), reassessment (147) and search-related block assessment (153A/153C). Faceless assessment removes territorial jurisdiction — notices are routed through the National Faceless Assessment Centre (NaFAC). A taxpayer aggrieved by an order may appeal successively before the Commissioner (Appeals), the Income-tax Appellate Tribunal (ITAT), the High Court on substantial question of law, and the Supreme Court by special leave.",
    },
    tables: [
      {
        title: "Types of Assessment",
        columns: ["Section", "Assessment", "Time Limit"],
        rows: [
          ["143(1)", "Summary — intimation", "9 months from FY end of return filing"],
          ["143(3)", "Scrutiny — regular", "12 months from AY end (18 months for TP)"],
          ["144", "Best-judgment (non-cooperation)", "Same as 143(3)"],
          ["147/148", "Reassessment", "3 yrs (10 yrs if income > ₹50 L escapement)"],
          ["153A/153C", "Search assessment", "12 months from search completion"],
        ],
      },
      {
        title: "Appellate Ladder — Time Limits",
        columns: ["Forum", "Time to File", "Fee"],
        rows: [
          ["CIT (Appeals)", "30 days from receipt of order", "₹250 – ₹1,000"],
          ["ITAT", "60 days from CIT(A) order", "₹500 – 1% of assessed income (max ₹10,000)"],
          ["High Court", "120 days from ITAT order", "As per HC Rules"],
          ["Supreme Court", "90 days via SLP", "As per SC Rules"],
        ],
      },
    ],
    checklist: [
      "Respond to Sec. 143(1) intimation within 30 days to avoid recovery.",
      "Sec. 143(2) notice can be issued only within 3 months of FY-end of return filing.",
      "Under faceless regime, all replies must be uploaded on the e-portal — no personal appearance.",
      "Preserve documentary evidence (bills, contracts, bank statements) for 6-8 years.",
      "For TP cases, references to TPO extend limitation to 21 months.",
      "Draft assessment order requires DRP option — file objections within 30 days.",
      "Interest u/s 220(2) accrues @ 1% p.m. on demand outstanding.",
      "Stay of demand — 20% payment plus application before AO/CIT(A)/ITAT.",
    ],
    forms: [
      "Form 35 — Appeal to CIT(A)",
      "Form 36 — Appeal to ITAT",
      "Form 3 — Vivad Se Vishwas (settlement, when reopened)",
    ],
    portals: [
      { label: "Income Tax e-Filing (faceless appeals)", url: "https://www.incometax.gov.in" },
      { label: "ITAT", url: "https://itat.gov.in" },
    ],
  },

  // ============== GST ==============
  "gst:Registration": {
    authority: "CBIC",
    hero: {
      title: "GST Registration",
      description:
        "Thresholds, procedure, voluntary registration and cancellation of GST registration.",
    },
    summary: {
      governing: "Sections 22-30, CGST Act, 2017 · Rule 8-25 CGST Rules",
      authority: "Central Board of Indirect Taxes & Customs (CBIC)",
      effective: "Threshold enhanced by Notification 10/2019-CT and later amendments",
      overview:
        "Every person engaged in taxable supplies with aggregate turnover crossing the prescribed threshold must register. Registration is state-specific — a person operating from multiple states must obtain separate registrations. The default threshold is ₹40 lakh for goods and ₹20 lakh for services (₹20 lakh / ₹10 lakh in special-category states). Compulsory registration applies irrespective of turnover for inter-state suppliers, e-commerce operators, casual taxable persons, non-resident taxable persons, and TDS/TCS deductors. Registration is granted within 3 working days of submitting Form REG-01 with due documents; Aadhaar authentication is now mandatory.",
    },
    tables: [
      {
        title: "Registration Thresholds (Aggregate Turnover)",
        columns: ["Supply", "Normal State", "Special Category*"],
        rows: [
          ["Goods only", "₹40 lakh", "₹20 lakh"],
          ["Services only", "₹20 lakh", "₹10 lakh"],
          ["Mixed goods + services", "₹20 lakh", "₹10 lakh"],
        ],
      },
      {
        title: "Compulsory Registration (Irrespective of Turnover)",
        columns: ["Trigger"],
        rows: [
          ["Inter-state taxable supply of goods (services exempt up to threshold)"],
          ["Casual taxable person / Non-resident taxable person"],
          ["E-commerce operator (Sec. 24)"],
          ["Person required to deduct TDS (Sec. 51) or collect TCS (Sec. 52)"],
          ["Reverse-charge liability under Sec. 9(3)/(4)"],
          ["Input Service Distributor (ISD)"],
        ],
      },
    ],
    checklist: [
      "Apply within 30 days of becoming liable — Form REG-01 on gst.gov.in.",
      "Aadhaar authentication mandatory (else physical verification triggered).",
      "Keep PAN, place of business proof, bank details, promoter photos ready.",
      "Special-category: Arunachal, Assam, Manipur, Meghalaya, Mizoram, Nagaland, Sikkim, Tripura, Uttarakhand, HP, J&K, Ladakh.",
      "Casual taxable person must deposit estimated tax in advance before REG-01.",
      "Voluntary registration allowed even below threshold (then all obligations attract).",
      "Amendments in core fields (name, address) via REG-14; approval required.",
      "Cancellation via REG-16; final return GSTR-10 within 3 months.",
    ],
    forms: [
      "REG-01 — Application",
      "REG-06 — Registration Certificate",
      "REG-14 — Amendment",
      "REG-16 — Cancellation request",
      "REG-11 — Extension for casual taxable person",
    ],
    portals: [
      { label: "GST Portal", url: "https://www.gst.gov.in" },
      { label: "CBIC — GST Circulars", url: "https://cbic-gst.gov.in" },
    ],
  },

  "gst:Returns & Filing": {
    authority: "CBIC",
    hero: {
      title: "GST Returns & Filing",
      description:
        "All return forms from GSTR-1 through GSTR-9C with due dates, applicability and late-fee structure.",
    },
    summary: {
      governing: "Sections 37-45, CGST Act, 2017 · Rule 59-84 CGST Rules",
      authority: "CBIC · GSTN",
      effective: "QRMP scheme from 01 Jan 2021; GSTR-1A introduced Aug 2024",
      overview:
        "Every registered taxpayer files periodic returns declaring outward supplies, tax liability and ITC utilisation. GSTR-1 captures outward supplies; GSTR-3B is the summary self-assessed return with tax payment. Composition dealers file CMP-08 quarterly and GSTR-4 annually. Annual return GSTR-9 (and reconciliation statement GSTR-9C for turnover > ₹5 crore) closes the year. Late filing attracts ₹50 per day (₹20 for nil), capped at ₹5,000 per return per Act.",
    },
    tables: [
      {
        title: "Return Calendar — Regular Taxpayer",
        columns: ["Return", "Frequency", "Due Date", "Applicability"],
        rows: [
          ["GSTR-1", "Monthly", "11th next month", "Outward supplies (TO > ₹5 cr)"],
          ["GSTR-1 (QRMP)", "Quarterly", "13th month after quarter", "TO ≤ ₹5 cr"],
          ["GSTR-1A", "Monthly (optional)", "Before GSTR-3B", "Amend/add invoices"],
          ["GSTR-3B", "Monthly", "20th next month", "Summary + tax payment"],
          ["GSTR-3B (QRMP)", "Quarterly", "22nd/24th (state-wise)", "TO ≤ ₹5 cr"],
          ["GSTR-5", "Monthly", "13th next month", "Non-resident"],
          ["GSTR-6", "Monthly", "13th next month", "Input Service Distributor"],
          ["GSTR-7", "Monthly", "10th next month", "TDS deductor"],
          ["GSTR-8", "Monthly", "10th next month", "E-commerce operator (TCS)"],
          ["GSTR-9", "Annual", "31 Dec of next FY", "TO > ₹2 cr (waived below)"],
          ["GSTR-9C", "Annual", "31 Dec of next FY", "TO > ₹5 cr (self-certified)"],
          ["CMP-08", "Quarterly", "18th next month", "Composition dealer"],
          ["GSTR-4", "Annual", "30 Apr of next FY", "Composition dealer"],
        ],
      },
      {
        title: "Late Fees & Penalty",
        columns: ["Default", "Late Fee", "Interest"],
        rows: [
          ["GSTR-3B / GSTR-1", "₹50/day (₹20 nil), max ₹5,000", "18% p.a."],
          ["GSTR-9 / 9C", "₹200/day, max 0.25% of TO", "—"],
          ["Non-filing 6 months", "Registration liable to cancellation (Sec. 29)", "—"],
        ],
      },
    ],
    checklist: [
      "Reconcile GSTR-1 vs books before filing to avoid GSTR-3B mismatch.",
      "Verify auto-populated GSTR-2B on 14th of every month — this is the ITC ceiling.",
      "QRMP scheme: pay tax monthly via PMT-06 and file GSTR-1/3B quarterly.",
      "IFF (Invoice Furnishing Facility) optional under QRMP — upload B2B invoices monthly.",
      "GSTR-3B once filed cannot be revised; correct via subsequent return.",
      "E-invoice mandatory for B2B if aggregate TO > ₹5 cr (from Aug-2023).",
      "Nil returns must also be filed — non-filing prevents subsequent returns.",
      "GSTR-9 reconciles annual figures — capture ITC reversals, RCM, exports.",
    ],
    forms: [
      "GSTR-1 / 1A — Outward supplies",
      "GSTR-3B — Summary return",
      "GSTR-9 / 9C — Annual & reconciliation",
      "CMP-08 / GSTR-4 — Composition",
      "PMT-06 — Tax payment (QRMP)",
    ],
    portals: [
      { label: "GST Portal", url: "https://www.gst.gov.in" },
      { label: "e-Invoice", url: "https://einvoice1.gst.gov.in" },
    ],
  },

  "gst:Input Tax Credit": {
    authority: "CBIC",
    hero: {
      title: "Input Tax Credit — Sections 16 & 17",
      description:
        "Eligibility conditions, blocked credits, GSTR-2B matching, reversal rules and common ITC mistakes.",
    },
    summary: {
      governing: "Sections 16-21, CGST Act, 2017 · Rules 36-45",
      authority: "CBIC · GSTN",
      effective: "Section 16(4) time limit + GSTR-2B matching (Rule 36(4)) since 2022",
      overview:
        "ITC allows a registered taxpayer to reduce output GST liability by the tax paid on inward supplies. Four conditions under Section 16(2) must be met: possession of tax invoice, receipt of goods/services, tax actually paid by supplier, and return filed. From 2022, ITC is available only if the invoice appears in GSTR-2B (Sec. 16(2)(aa)). Blocked credits under Section 17(5) — such as motor vehicles, food & beverages, personal consumption — cannot be availed. ITC on inputs used partly for exempt supplies must be reversed proportionately under Rule 42/43.",
    },
    tables: [
      {
        title: "Blocked Credits — Section 17(5)",
        columns: ["Blocked Item"],
        rows: [
          ["Motor vehicles ≤ 13 seater (with limited business exceptions)"],
          ["Food, beverages, outdoor catering (unless statutorily provided)"],
          ["Health services, cosmetic and plastic surgery"],
          ["Club, health & fitness centre memberships"],
          ["Life & health insurance (except statutory)"],
          ["Works-contract services for immovable property (except plant & machinery)"],
          ["Goods/services for personal consumption"],
          ["Goods lost, stolen, destroyed, written off, given as gifts/free samples"],
        ],
      },
    ],
    checklist: [
      "Verify supplier's GSTIN & GSTR-1 filing before claiming ITC.",
      "Match every invoice with GSTR-2B — mismatches lead to Rule 88C notices.",
      "Time limit — earlier of 30 November of next FY or annual return filing (Sec. 16(4)).",
      "Rule 42/43 reversal for common inputs used in taxable + exempt supplies.",
      "Reversal on non-payment to supplier within 180 days (Rule 37) — re-claim after payment.",
      "ITC on capital goods spread over 60 months for common credit computation.",
      "Blocked credit list is exhaustive — do not attempt workarounds via reimbursement.",
      "Documentary evidence: tax invoice, e-way bill, GRN/consignment note.",
    ],
    forms: [
      "GSTR-2B — Auto-drafted ITC statement",
      "ITC-01 — First-time ITC on stock (fresh registration)",
      "ITC-02 — ITC transfer (business transfer / merger)",
      "ITC-03 — Reversal on switching to composition / exempt",
      "ITC-04 — Job-work goods movement",
    ],
    portals: [
      { label: "GST Portal", url: "https://www.gst.gov.in" },
    ],
  },

  "gst:E-Way Bill": {
    authority: "CBIC",
    hero: {
      title: "E-Way Bill",
      description:
        "Mandatory electronic waybill for movement of goods worth > ₹50,000.",
    },
    summary: {
      governing: "Rule 138 & 138A, CGST Rules, 2017",
      authority: "CBIC · NIC (ewaybillgst.gov.in)",
      effective: "Nationwide 01 April 2018 (inter-state) / June 2018 (intra-state)",
      overview:
        "An e-Way Bill is a document generated on the common portal before commencement of movement of goods of value exceeding ₹50,000. Both supplier/recipient and transporter must generate/complete Part-A and Part-B (vehicle number). The bill is valid for 1 day per 200 km (or part) for regular cargo (1 day per 20 km for over-dimensional cargo). Movement without e-way bill attracts penalty up to the amount of tax evaded or ₹10,000 whichever is higher (Sec. 129).",
    },
    tables: [
      {
        title: "E-Way Bill Validity",
        columns: ["Distance (Regular)", "Validity"],
        rows: [
          ["Up to 200 km", "1 day"],
          ["Every additional 200 km or part", "1 additional day"],
          ["Over-dimensional / Multimodal cargo", "1 day per 20 km"],
        ],
      },
    ],
    checklist: [
      "Generate e-way bill before movement — via portal / API / SMS.",
      "Not required for consignment value ≤ ₹50,000 (state-specific relaxations for intra-state).",
      "Not required for exempt goods (schedule III), transport by non-motorised conveyance.",
      "Part-B must be updated within 15 days of Part-A (else EWB invalid).",
      "Cancellation possible within 24 hours if goods not moved.",
      "Validity extendable within 8 hours before or after expiry for delays.",
      "Consolidated e-way bill (EWB-02) for multiple consignments in one vehicle.",
      "Penalty u/s 129 — tax + equal penalty; goods & vehicle can be detained.",
    ],
    forms: [
      "EWB-01 — E-Way Bill",
      "EWB-02 — Consolidated E-Way Bill",
      "EWB-03 — Verification report",
      "EWB-04 — Report of detention",
    ],
    portals: [
      { label: "E-Way Bill Portal", url: "https://ewaybillgst.gov.in" },
    ],
  },

  "gst:Composition Scheme": {
    authority: "CBIC",
    hero: {
      title: "Composition Scheme",
      description:
        "Simplified scheme for small taxpayers — flat tax, no ITC, quarterly payment.",
    },
    summary: {
      governing: "Section 10, CGST Act, 2017 · Rule 3-7 CGST Rules",
      authority: "CBIC",
      effective: "Turnover cap enhanced to ₹1.5 cr (₹75 lakh in special-category states)",
      overview:
        "The composition scheme allows small taxpayers to pay tax at a flat percentage of turnover without ITC, and file simplified returns. Eligibility: aggregate turnover in preceding FY ≤ ₹1.5 crore (₹75 lakh in special-category states). A separate scheme under Section 10(2A) exists for services & mixed suppliers up to ₹50 lakh at 6%. Composition dealers cannot make inter-state outward supplies, supply through e-commerce, or claim ITC. They must display 'composition taxable person' on invoices and place of business.",
    },
    tables: [
      {
        title: "Composition Tax Rates",
        columns: ["Category", "Rate (CGST + SGST)"],
        rows: [
          ["Manufacturer", "1% (0.5% + 0.5%)"],
          ["Trader / Reseller", "1% on taxable turnover"],
          ["Restaurant (no alcohol)", "5% (2.5% + 2.5%)"],
          ["Service supplier — Sec. 10(2A)", "6% (3% + 3%)"],
        ],
      },
    ],
    checklist: [
      "Aggregate turnover ≤ ₹1.5 cr (₹75 lakh SCS) for goods; ≤ ₹50 lakh for services.",
      "Cannot make inter-state outward supplies (inter-state inward permitted).",
      "Cannot supply through e-commerce operator required to collect TCS.",
      "Cannot claim ITC or collect tax from customers.",
      "File CMP-08 quarterly (18th) + GSTR-4 annually (30 Apr).",
      "Opt-in via CMP-02 before start of FY; opt-out via CMP-04 during the year.",
      "Mention 'composition taxable person, not eligible to collect tax' on bill of supply.",
      "RCM inward supplies still attract normal rate + no ITC — factor into pricing.",
    ],
    forms: [
      "CMP-02 — Intimation to opt in",
      "CMP-03 — Stock intimation (from regular → composition)",
      "CMP-04 — Withdrawal from scheme",
      "CMP-08 — Quarterly self-assessed statement",
      "GSTR-4 — Annual return",
    ],
    portals: [
      { label: "GST Portal", url: "https://www.gst.gov.in" },
    ],
  },

  "gst:GST Audit": {
    authority: "CBIC",
    hero: {
      title: "GST Audit & Reconciliation",
      description:
        "Departmental audits, taxpayer reconciliation via GSTR-9C, and common audit triggers.",
    },
    summary: {
      governing: "Sections 35(5), 65, 66, CGST Act, 2017",
      authority: "CBIC (departmental) · Taxpayer (self-certified 9C)",
      effective: "Self-certified 9C from FY 2020-21 (audit by CA/CMA discontinued)",
      overview:
        "GST audits are of three types — (i) Section 65 departmental audit conducted by CBIC officers on selected registrations, (ii) Section 66 special audit ordered by the Commissioner where accounts are complex, and (iii) taxpayer-initiated reconciliation via GSTR-9C for turnover exceeding ₹5 crore. GSTR-9C is now self-certified (earlier required CA/CMA certification). Common triggers for departmental audit include ITC-turnover mismatch, RCM defaults, high refund claims, low tax vs turnover ratio, and e-way bill anomalies.",
    },
    tables: [
      {
        title: "Audit Types",
        columns: ["Type", "Section", "Trigger", "Time Limit"],
        rows: [
          ["Departmental Audit", "65", "Selection by Commissioner", "3 months (extendable to 6)"],
          ["Special Audit", "66", "Complex records", "90 days"],
          ["Reconciliation (9C)", "44 + Rule 80", "Turnover > ₹5 cr", "31 Dec of next FY"],
        ],
      },
    ],
    checklist: [
      "Maintain records for 72 months from due date of annual return.",
      "GSTR-9C — reconcile audited financials with GSTR-9 disclosures.",
      "Report ITC availed vs eligible, tax paid, refund claimed, HSN summary.",
      "Common triggers: ITC utilisation > 95%, low output rate, RCM defaults.",
      "Notice ADT-01 initiates departmental audit — respond within 15 days.",
      "Special audit under Sec. 66 borne by taxpayer (cost fixed by Commissioner).",
      "Voluntary disclosure of discrepancies through DRC-03 to reduce penalty.",
      "Follow up on audit observations via ADT-02 within stipulated time.",
    ],
    forms: [
      "GSTR-9 — Annual return",
      "GSTR-9C — Reconciliation statement",
      "ADT-01 / 02 / 03 — Audit notices & reports",
      "DRC-03 — Voluntary payment",
    ],
    portals: [
      { label: "GST Portal", url: "https://www.gst.gov.in" },
      { label: "CBIC", url: "https://cbic-gst.gov.in" },
    ],
  },

  // ============== COMPANY & SEBI LAWS ==============
  "company-sebi:Companies Act 2013": {
    authority: "MCA",
    hero: {
      title: "Companies Act 2013 — Key Compliances",
      description:
        "Annual and event-based compliances for private, public, OPC, and LLP entities.",
    },
    summary: {
      governing: "Companies Act, 2013 (and LLP Act, 2008 for LLPs)",
      authority: "Ministry of Corporate Affairs · Registrar of Companies (ROC)",
      effective: "Act commenced 12 Sep 2013; multiple amendments through 2023",
      overview:
        "The Companies Act 2013 codifies incorporation, governance, disclosures, audit and winding-up of Indian companies. Every registered company must hold board and shareholder meetings, maintain statutory registers, and file annual returns and financial statements with the ROC. Non-compliance attracts fines and, in serious cases, prosecution and director disqualification. LLPs follow the parallel LLP Act, 2008 with lighter compliance and no board meeting requirement.",
    },
    tables: [
      {
        title: "Annual Filings — Companies (FY 2024-25)",
        columns: ["Form", "Purpose", "Due Date", "Applicable To"],
        rows: [
          ["AOC-4", "Financial statements", "30 days from AGM", "All companies"],
          ["MGT-7 / 7A", "Annual return", "60 days from AGM", "All / OPC & small co."],
          ["DPT-3", "Deposits / loans return", "30 June", "All companies"],
          ["MSME-1", "Payments to MSMEs > 45 days", "Half-yearly (30 Apr / 31 Oct)", "All companies"],
          ["DIR-3 KYC", "Director KYC", "30 September", "All DIN holders"],
          ["ADT-1", "Auditor appointment intimation", "15 days from AGM", "All companies"],
        ],
      },
      {
        title: "Annual Filings — LLP",
        columns: ["Form", "Purpose", "Due Date"],
        rows: [
          ["Form 11", "Annual return", "30 May"],
          ["Form 8", "Statement of Accounts & Solvency", "30 October"],
        ],
      },
    ],
    checklist: [
      "Hold first board meeting within 30 days of incorporation.",
      "Appoint statutory auditor within 30 days (ratification not required from CY 22 amendment).",
      "Convene AGM within 6 months of FY end (max 15 months gap between AGMs).",
      "Maintain statutory registers — MBP-2, MBP-3, MGT-1, SH-2 etc.",
      "File AOC-4 & MGT-7 within statutory windows to avoid ₹100/day late fee.",
      "OPC — one director's resolution replaces board meeting.",
      "Private co. exemption — Sec. 90 (SBO), 117 (resolutions) relaxations still apply.",
      "Section 8 companies — file MGT-14 for special resolutions within 30 days.",
    ],
    forms: [
      "AOC-4 / MGT-7 / MGT-7A",
      "DPT-3 · MSME-1 · DIR-3 KYC · ADT-1",
      "SH-7 (capital), PAS-3 (allotment), MGT-14 (resolutions)",
      "LLP: Form 8 / Form 11",
    ],
    portals: [
      { label: "MCA V3 Portal", url: "https://www.mca.gov.in" },
    ],
  },

  "company-sebi:ROC Filings": {
    authority: "MCA",
    hero: {
      title: "ROC Filings Calendar",
      description:
        "Every ROC form, due date and late fee — organised for private, public, OPC and LLP.",
    },
    summary: {
      governing: "Companies Act 2013 & Rules · LLP Act 2008",
      authority: "Registrar of Companies (State ROC)",
      effective: "V3 portal for all filings from 2022 onwards",
      overview:
        "The ROC calendar is a rolling set of periodic and event-based filings. Missing due dates attracts ₹100 per day per form under the additional-fee regime for company forms and up to ₹100/day (max ₹5 lakh) for LLPs. Repeated defaults invite director disqualification (Section 164(2)) and strike-off proceedings under Section 248.",
    },
    tables: [
      {
        title: "Annual ROC Calendar",
        columns: ["Compliance", "Frequency", "Due Date", "Applicable Entity"],
        rows: [
          ["ADT-1 (Auditor appointment)", "5-yearly + on change", "15 days from AGM", "All co."],
          ["DIR-3 KYC / KYC-Web", "Annual", "30 September", "All DIN holders"],
          ["DPT-3", "Annual", "30 June", "All co. with deposits/loans"],
          ["MSME-1", "Half-yearly", "30 Apr / 31 Oct", "All co."],
          ["Form 8 (LLP)", "Annual", "30 October", "All LLPs"],
          ["Form 11 (LLP)", "Annual", "30 May", "All LLPs"],
          ["AOC-4", "Annual", "30 days from AGM (usu. 30 Oct)", "All co."],
          ["AOC-4 XBRL", "Annual", "30 days from AGM", "Listed / TO ≥ ₹100 cr"],
          ["MGT-7 / 7A", "Annual", "60 days from AGM (usu. 29 Nov)", "All co. / OPC & small co."],
          ["BEN-2", "Event", "30 days of SBO change", "Where SBO exists"],
          ["MGT-14", "Event", "30 days of resolution", "Special resolutions"],
        ],
      },
      {
        title: "Late Fee Structure — Companies",
        columns: ["Delay Period", "Additional Fee"],
        rows: [
          ["Up to 30 days", "2x normal fee"],
          ["31-60 days", "4x normal fee"],
          ["61-90 days", "6x normal fee"],
          ["91-180 days", "10x normal fee"],
          ["> 180 days", "12x normal fee"],
        ],
      },
    ],
    checklist: [
      "Reconcile board resolutions to ensure every event-based form is filed within 30 days.",
      "Track DSC validity of all signatories — expired DSC blocks filing.",
      "Maintain digital minutes register and share proofs while filing.",
      "OPC / small company can file MGT-7A (simplified) — verify eligibility annually.",
      "IEPF-2 (unpaid dividend statement) required if any unpaid dividend outstanding > 7 yrs.",
      "Prepare board-approved CSR-2 for companies covered under Sec. 135.",
      "LLP Form 8 requires solvency certificate signed by two designated partners.",
      "Verify PAN, mobile & email in DIR-3 KYC-Web every year (deactivation if lapsed).",
    ],
    forms: [
      "AOC-4 · MGT-7 / 7A · DPT-3 · MSME-1 · DIR-3 KYC",
      "ADT-1 · BEN-2 · MGT-14 · PAS-3 · SH-7",
      "CSR-2 · IEPF-2 / 4 / 7",
      "LLP Form 3 / 4 / 8 / 11 / 14",
    ],
    portals: [
      { label: "MCA V3 Portal", url: "https://www.mca.gov.in" },
    ],
  },

  "company-sebi:Board Meetings": {
    authority: "MCA",
    hero: {
      title: "Board Meetings — Sections 173-175",
      description:
        "Minimum frequency, quorum, notice, minutes and video-conferencing rules.",
    },
    summary: {
      governing: "Sections 173-175, 118, Companies Act 2013 · Secretarial Standard SS-1",
      authority: "MCA · ICSI (secretarial standards)",
      effective: "Video conferencing permitted post 2014 amendment",
      overview:
        "The Board is the operational governing body of a company. Section 173 mandates a minimum of 4 board meetings per calendar year with no gap exceeding 120 days between two meetings (2 meetings for OPC / small / dormant / start-up companies). Notice of at least 7 days is required, shorter notice permitted with consent for urgent matters. Video conferencing is fully permitted (except for approval of financial statements, board report, merger scheme, prospectus etc. — restricted matters). Minutes must be prepared within 15 days, entered in the minutes book within 30 days, and signed by chairman.",
    },
    tables: [
      {
        title: "Board Meeting Norms",
        columns: ["Requirement", "Standard Co.", "OPC / Small / Startup / Dormant"],
        rows: [
          ["Minimum meetings/year", "4", "2"],
          ["Max gap between meetings", "120 days", "≥ 90 days"],
          ["Notice period", "7 days", "7 days"],
          ["Quorum", "1/3rd or 2 (whichever higher)", "Only directors"],
          ["Video conferencing", "Allowed (with restrictions)", "Allowed"],
          ["Minutes book", "Signed within 30 days", "Signed within 30 days"],
        ],
      },
    ],
    checklist: [
      "Prepare agenda + explanatory notes 7 days in advance; circulate with notice.",
      "Verify quorum at start and throughout (attendance by VC counted for quorum).",
      "Record dissents in minutes; interested directors abstain from voting.",
      "Restricted matters (financials, mergers) cannot be transacted only via VC.",
      "Chairman signs each page of minutes; tampering is punishable u/s 118(11).",
      "Circular resolutions (Sec. 175) — 2/3rd of directors' approval required.",
      "Committee meetings (Audit, Nomination, CSR) follow parallel SS-1 rules.",
      "Maintain attendance register + record of participation via VC.",
    ],
    forms: [
      "SS-1 (Secretarial Standard on Board Meetings)",
      "MGT-14 — Filing of resolutions (where applicable)",
    ],
    portals: [
      { label: "MCA V3", url: "https://www.mca.gov.in" },
      { label: "ICSI", url: "https://www.icsi.edu" },
    ],
  },

  "company-sebi:SEBI Regulations": {
    authority: "SEBI",
    hero: {
      title: "SEBI LODR — Listing Obligations",
      description:
        "Quarterly compliance calendar and disclosure requirements for listed entities.",
    },
    summary: {
      governing: "SEBI (Listing Obligations & Disclosure Requirements) Regulations, 2015",
      authority: "Securities and Exchange Board of India",
      effective: "01 December 2015; multiple amendments through 2024",
      overview:
        "The LODR regulations codify continuous disclosure and corporate-governance requirements for listed entities. Regulation 17-27 cover the board of directors, independent directors, committees, and related-party transactions. Regulations 30 & 30A require material events disclosure within 12/24 hours. Financial results are filed quarterly (Reg. 33) — within 45 days of quarter end and 60 days for the year end. Non-compliance attracts fines from Stock Exchanges, adjudication proceedings, and freezing of promoter shares.",
    },
    tables: [
      {
        title: "Quarterly Compliance Calendar",
        columns: ["Filing", "Frequency", "Due Date"],
        rows: [
          ["Financial results (Reg. 33)", "Quarterly", "45 days from quarter end (Q4: 60 days)"],
          ["Corporate Governance Report (Reg. 27(2))", "Quarterly", "21 days from quarter end"],
          ["Shareholding pattern (Reg. 31)", "Quarterly", "21 days from quarter end"],
          ["Investor complaints (Reg. 13(3))", "Quarterly", "21 days from quarter end"],
          ["Related Party Transactions", "Half-yearly", "30 days from half-year end"],
          ["Annual Report + BRSR", "Annual", "As per companies act + LODR"],
          ["Secretarial Audit Report", "Annual", "60 days of FY end"],
        ],
      },
    ],
    checklist: [
      "Board composition — at least 1/3rd independent directors; 1/2 if chairperson is executive.",
      "Woman independent director mandatory (top 1000 by market cap).",
      "Audit Committee: 2/3rd independent, chair by independent director.",
      "Material event disclosure within 12 hours if from within, 24 hours from outside.",
      "Related-party transactions above material threshold need shareholders' approval.",
      "BRSR Core (assurance) for top 250 by market cap from FY 26-27.",
      "Quarterly results signed by CEO/CFO; auditor's LR by CA firm.",
      "Insider Trading Code (SDDs, trading window) integrates with LODR.",
    ],
    forms: [
      "Financial Results (Reg. 33 template)",
      "Corporate Governance Report (Annexure-I)",
      "BRSR (Business Responsibility & Sustainability Report)",
      "Secretarial Audit Report (MR-3)",
    ],
    portals: [
      { label: "SEBI", url: "https://www.sebi.gov.in" },
      { label: "BSE Listing Centre", url: "https://listing.bseindia.com" },
      { label: "NSE NEAPS", url: "https://www.connect2nse.com/neaps" },
    ],
  },

  "company-sebi:Insider Trading": {
    authority: "SEBI",
    hero: {
      title: "SEBI PIT Regulations 2015",
      description:
        "UPSI definition, trading window closure, pre-clearance and penalties.",
    },
    summary: {
      governing: "SEBI (Prohibition of Insider Trading) Regulations, 2015",
      authority: "SEBI",
      effective: "15 May 2015; amended in 2018, 2019, 2020 (Structured Digital Database)",
      overview:
        "The PIT Regulations prohibit any insider from trading in securities of a listed company while in possession of Unpublished Price Sensitive Information (UPSI). UPSI includes financial results, dividend, capital changes, changes in KMP, material contracts, and other information reasonably expected to affect the price. Every listed entity must maintain a Structured Digital Database (SDD) capturing every UPSI sharing incident. Trading windows must be closed at least 48 hours before result announcement till 48 hours after. Designated persons (DPs) require pre-clearance for trades above ₹10 lakh per calendar quarter.",
    },
    tables: [
      {
        title: "Trading Window & Pre-clearance",
        columns: ["Event", "Window Status"],
        rows: [
          ["Quarterly financial results", "Closed 48 hrs before to 48 hrs after"],
          ["Dividend / share buyback", "Closed till 48 hrs after announcement"],
          ["Material corporate action", "Closed at Board decision"],
          ["Pre-clearance", "Required if aggregate trades > ₹10 L / quarter"],
          ["Contra-trade cooling", "6-month restriction on opposite side"],
        ],
      },
    ],
    checklist: [
      "Board must adopt Code of Conduct & Fair Disclosure under Schedule B/C of PIT.",
      "Maintain SDD capturing every UPSI transmission with time-stamp (immutable).",
      "Compliance Officer approves pre-clearance requests within 48 hours.",
      "Insider must execute trade within 7 trading days of pre-clearance.",
      "Contra-trade cooling period is 6 months.",
      "Chinese wall between UPSI-handling and trading personnel.",
      "Disgorgement of profits + penalty up to ₹25 cr or 3x profit (whichever higher).",
      "Annual DP declaration + immediate reporting if UPSI is unintentionally shared.",
    ],
    forms: [
      "Form B/C/D — Initial & continuing DP disclosures",
      "Pre-clearance application (per company code)",
    ],
    portals: [
      { label: "SEBI", url: "https://www.sebi.gov.in" },
    ],
  },

  "company-sebi:Listing Obligations": {
    authority: "SEBI",
    hero: {
      title: "Corporate Governance & Listing Obligations",
      description:
        "Governance norms, RPT disclosures, annual report requirements and secretarial audit applicability.",
    },
    summary: {
      governing: "SEBI (LODR) Regulations 2015 · Companies Act 2013 · SS-1/2",
      authority: "SEBI · MCA",
      effective: "LODR as amended by SEBI (LODR) (Amendment) 2023",
      overview:
        "Listing obligations extend beyond quarterly filings into board composition, related-party transactions, disclosure of material events and annual report content. RPTs above 10% of consolidated turnover need prior shareholder approval; audit committee scrutinises every RPT. The annual report must include the corporate governance report, BRSR, management discussion and analysis, and directors' report. Secretarial audit under MR-3 applies to listed entities and public companies meeting prescribed thresholds.",
    },
    tables: [
      {
        title: "Key Disclosure Timelines",
        columns: ["Disclosure", "Timeline"],
        rows: [
          ["Material event — from within", "12 hours"],
          ["Material event — from outside", "24 hours"],
          ["Annual report on website", "21 working days of AGM"],
          ["RPT half-yearly report", "30 days from half-year"],
          ["Voting results", "2 working days"],
        ],
      },
    ],
    checklist: [
      "Ensure 1/3rd (or 1/2) independent directors on the board.",
      "At least one woman director (independent for top 1000).",
      "Constitute Audit, Nomination & Remuneration, Stakeholders' Relationship and Risk Management Committees.",
      "RPT policy approved by audit committee; publish on website.",
      "Every RPT above materiality threshold — prior shareholder approval.",
      "Annual report to include Corporate Governance Report + BRSR.",
      "Secretarial audit report (MR-3) annexed to Directors' Report.",
      "Prompt disclosure of trading window closure and re-opening.",
    ],
    forms: [
      "Annual Report (including CG report, BRSR, MDA, Directors' Report)",
      "MR-3 — Secretarial Audit Report",
      "Notices for shareholder meetings under Sec. 101 CA 2013",
    ],
    portals: [
      { label: "SEBI", url: "https://www.sebi.gov.in" },
      { label: "BSE Listing Centre", url: "https://listing.bseindia.com" },
      { label: "NSE NEAPS", url: "https://www.connect2nse.com/neaps" },
    ],
  },

  // ============== FEMA & FCRA ==============
  "fema-fcra:Foreign Direct Investment": {
    authority: "RBI / DPIIT",
    hero: {
      title: "Foreign Direct Investment",
      description:
        "Sectoral limits, automatic vs approval route, pricing and reporting.",
    },
    summary: {
      governing: "FEMA 1999 · FEM (Non-Debt Instruments) Rules 2019 · FDI Policy 2020",
      authority: "RBI (regulator) · DPIIT (policy) · Sectoral Ministries",
      effective: "Consolidated FDI Policy 2020 with regular press notes",
      overview:
        "FDI is permitted in most sectors under the Automatic Route (no prior approval) up to sectoral caps. Sensitive sectors — defence, telecom, media, banking — require Government approval (Approval Route) through DPIIT's FDI Portal. Prohibited sectors include lottery, gambling, chit funds, real estate business (except construction), and tobacco manufacture. Post-investment, the Indian investee company must file Form FC-GPR within 30 days of allotment and Form FC-TRS within 60 days of transfer. Pricing must comply with FEMA pricing guidelines (fair value from a merchant banker or CA).",
    },
    tables: [
      {
        title: "Illustrative Sectoral Caps (Aug 2025)",
        columns: ["Sector", "Cap", "Route"],
        rows: [
          ["Manufacturing", "100%", "Automatic"],
          ["IT / ITES", "100%", "Automatic"],
          ["Insurance", "74%", "Automatic"],
          ["Banking (Private)", "74%", "Auto up to 49%; Govt above"],
          ["Defence", "74%", "Auto up to 74% (100% if state-of-art)"],
          ["Print media (news)", "26%", "Approval"],
          ["Broadcasting (Digital news)", "26%", "Approval"],
          ["Telecom", "100%", "Automatic"],
          ["Multi-brand retail", "51%", "Approval"],
          ["E-commerce (marketplace)", "100%", "Automatic (inventory model restricted)"],
          ["Space (Launch vehicles)", "49%", "Automatic (higher via approval)"],
          ["Prohibited", "0%", "Lottery, gambling, chit funds, RE business, tobacco"],
        ],
      },
      {
        title: "Reporting Timelines",
        columns: ["Form", "Purpose", "Due Date"],
        rows: [
          ["FC-GPR", "Allotment to non-resident", "30 days of allotment"],
          ["FC-TRS", "Transfer between resident and non-resident", "60 days of transfer / receipt"],
          ["FLA Return", "Annual assets & liabilities disclosure", "15 July"],
          ["Form ODI", "Overseas direct investment", "Before remittance"],
          ["Form ESOP", "ESOP to non-residents", "30 days"],
          ["Form LLP (I) / (II)", "FDI in LLPs", "30 / 60 days"],
        ],
      },
    ],
    checklist: [
      "Confirm sector cap & route (Auto / Approval) via Consolidated FDI Policy.",
      "Ensure pricing per FEMA pricing guidelines (DCF / NAV / SEBI-registered method).",
      "Instrument must be permitted (equity, CCPS, CCD, warrants) — LLPs restricted to equity.",
      "File FC-GPR on FIRMS portal (RBI) within 30 days of allotment.",
      "File FC-TRS for every transfer of instruments between R and NR.",
      "FLA return to be filed by every Indian company holding FDI or ODI (15 July).",
      "Downstream investment through Indian company: File Form DI.",
      "Prohibited sector list — verify before any inbound investment.",
    ],
    forms: [
      "FC-GPR · FC-TRS · FLA · Form ODI · Form DI",
      "Form ESOP · Form LLP(I)/(II) · Form Convertible Notes",
    ],
    portals: [
      { label: "RBI FIRMS Portal", url: "https://firms.rbi.org.in" },
      { label: "DPIIT — FDI Policy", url: "https://dpiit.gov.in" },
      { label: "FDI Approval Portal", url: "https://fifp.gov.in" },
    ],
  },

  "fema-fcra:External Commercial Borrowings": {
    authority: "RBI",
    hero: {
      title: "External Commercial Borrowings (ECB)",
      description:
        "Eligible borrowers, minimum maturity, end-use restrictions and reporting.",
    },
    summary: {
      governing: "FEMA (Borrowing or Lending) Regulations, 2018 · ECB Framework 2019",
      authority: "Reserve Bank of India",
      effective: "Consolidated ECB Framework — Master Direction 5/2018-19",
      overview:
        "ECBs are commercial loans raised by eligible resident entities from recognised non-resident lenders. The framework distinguishes Foreign Currency (FCY) and Rupee-denominated (INR) ECBs. Automatic route: up to USD 750 million per FY per borrower with 3-year minimum average maturity (5 years for certain end-uses). All-in-cost ceiling capped at benchmark rate + 550 bps. Certain end-uses — real estate, chit funds, capital market speculation — are prohibited. Every ECB must be reported via Form ECB and Monthly Form ECB-2.",
    },
    tables: [
      {
        title: "ECB Framework Key Parameters",
        columns: ["Parameter", "Automatic Route"],
        rows: [
          ["Annual limit per borrower", "USD 750 million"],
          ["Minimum Average Maturity", "3 years (5-10 for specific uses)"],
          ["All-in-cost ceiling", "Benchmark + 550 bps"],
          ["Late reporting fee", "USD 100 per week (max USD 5,000)"],
          ["Prohibited end-uses", "Real estate, chit funds, equity investment, working capital > 3 yrs"],
        ],
      },
    ],
    checklist: [
      "Confirm eligibility — all entities eligible for FDI + specific categories permitted.",
      "Choose recognised lender — foreign equity holder, IBRD affiliate, multilaterals etc.",
      "Draft ECB terms respecting all-in-cost, maturity, currency norms.",
      "Obtain Loan Registration Number (LRN) from RBI before drawdown.",
      "File Form ECB before entering into agreement.",
      "File Form ECB-2 monthly by 7th of following month till full repayment.",
      "Monitor hedging — mandatory hedging norms for certain sector borrowings.",
      "End-use certification by statutory auditor annually.",
    ],
    forms: [
      "Form ECB — Loan Registration",
      "Form ECB-2 — Monthly return",
      "Form ECB — Cancellation / Change",
    ],
    portals: [
      { label: "RBI ECB Framework", url: "https://www.rbi.org.in" },
      { label: "FIRMS Portal", url: "https://firms.rbi.org.in" },
    ],
  },

  "fema-fcra:Overseas Direct Investment": {
    authority: "RBI",
    hero: {
      title: "Overseas Direct Investment (ODI)",
      description:
        "Financial commitment limits, prohibited investments and reporting.",
    },
    summary: {
      governing: "FEM (Overseas Investment) Rules 2022 · Directions 2022",
      authority: "Reserve Bank of India · Ministry of Finance",
      effective: "22 August 2022 (consolidated framework)",
      overview:
        "The 2022 framework replaces the earlier ODI Regulations and streamlines overseas investment into three buckets — ODI (equity in JV/WOS), OPI (Overseas Portfolio Investment, up to 10% listed) and Financial Commitment (equity + loans + guarantees). Under the Automatic Route, total financial commitment cannot exceed 400% of net worth. Investment in real estate business, banking (except through subsidiary), and financial products linked to Rupee are prohibited unless approved. Every ODI transaction is reported through Form FC / FC(A) on the RBI FIRMS portal.",
    },
    tables: [
      {
        title: "ODI Framework Highlights",
        columns: ["Parameter", "Automatic Route"],
        rows: [
          ["Financial Commitment cap", "400% of Net Worth"],
          ["Individual (LRS) cap", "USD 2,50,000 / FY"],
          ["Reporting form", "FC (Company) / FC-A (Individual)"],
          ["Annual Performance Report", "APR by 31 December"],
          ["Restricted / Prohibited", "RE biz, gambling, arms unless approved"],
        ],
      },
    ],
    checklist: [
      "Ensure host country is not in FATF/OECD non-cooperative list.",
      "Confirm activity of foreign entity is permissible under the ODI rules.",
      "Compute net-worth from latest audited balance sheet before financial commitment.",
      "Individuals utilise LRS route only; corporate ODI routed via AD Bank.",
      "File Form FC on FIRMS portal before remittance.",
      "APR every year by 31 December post investment.",
      "Disinvestment — settle via AD Bank; report in Form OPI-2.",
      "Guarantees: 100% financial commitment computed for cap.",
    ],
    forms: [
      "Form FC — Financial Commitment (Company)",
      "Form FC-A — LRS-linked ODI (Individual)",
      "Form OPI-2 — Disinvestment / restructuring",
      "APR — Annual Performance Report",
    ],
    portals: [
      { label: "RBI FIRMS Portal", url: "https://firms.rbi.org.in" },
    ],
  },

  "fema-fcra:FCRA Registration": {
    authority: "MHA",
    hero: {
      title: "FCRA Registration & Compliance",
      description:
        "Registration eligibility, designated bank account, annual returns and post-2020 amendments.",
    },
    summary: {
      governing: "Foreign Contribution (Regulation) Act, 2010 · Rules 2011",
      authority: "Ministry of Home Affairs · FCRA Wing",
      effective: "FCRA Amendment Act 2020 — SBI New Delhi designated account rule",
      overview:
        "FCRA regulates acceptance and utilisation of foreign contribution and hospitality by associations for definite cultural, economic, educational, religious or social programmes. Two routes exist: (i) Registration (long-term) and (ii) Prior Permission (project-specific). The 2020 amendment mandates a designated FCRA bank account with State Bank of India, Sansad Marg, New Delhi. Sub-granting of foreign contribution to other associations is now prohibited. Association must exist for at least 3 years and have spent at least ₹15 lakh on core activities.",
    },
    tables: [
      {
        title: "FCRA — Key Compliances",
        columns: ["Item", "Requirement"],
        rows: [
          ["Registration validity", "5 years (renewal 6 months before expiry)"],
          ["Designated FC account", "SBI Sansad Marg branch only"],
          ["Sub-granting", "Prohibited"],
          ["Administrative expense cap", "20% of foreign contribution"],
          ["Annual return", "FC-4 by 31 December"],
          ["Quarterly reporting", "On FCRA website"],
        ],
      },
    ],
    checklist: [
      "Association must be in existence for ≥ 3 years and have spent ≥ ₹15 lakh on activities.",
      "Registration via Form FC-3A; Prior permission via FC-3B.",
      "Only receive foreign contribution in the FCRA designated SBI account.",
      "Utilisation account can be any bank; funds move only after receipt into designated a/c.",
      "Renew registration 6 months before expiry via FC-3C.",
      "File FC-4 annual return by 31 December (with audited financials).",
      "Quarterly disclosure of foreign contribution received.",
      "Administrative expenditure must not exceed 20% of FC utilised.",
    ],
    forms: [
      "FC-3A — Registration",
      "FC-3B — Prior Permission",
      "FC-3C — Renewal",
      "FC-4 — Annual Return",
      "FC-6 — Change intimation",
    ],
    portals: [
      { label: "FCRA Online", url: "https://fcraonline.nic.in" },
      { label: "MHA", url: "https://www.mha.gov.in" },
    ],
  },

  "fema-fcra:Compounding": {
    authority: "RBI",
    hero: {
      title: "FEMA Compounding",
      description:
        "Voluntary settlement of FEMA contraventions before adjudication.",
    },
    summary: {
      governing: "Section 15, FEMA 1999 · FEM (Compounding Proceedings) Rules 2000",
      authority: "Reserve Bank of India · Enforcement Directorate (for certain matters)",
      effective: "Master Direction on Compounding, June 2024",
      overview:
        "Compounding is a voluntary process allowing a person who has contravened FEMA to settle the matter by paying a compounding fee, avoiding prosecution. Almost all FEMA contraventions (except money laundering-linked or those involving PMLA) can be compounded. Application is made in the prescribed form with a detailed representation, along with a ₹5,000 fee (non-refundable) via demand draft. RBI issues an order within 180 days.",
    },
    tables: [
      {
        title: "Compounding Fee Illustration",
        columns: ["Contravention Amount", "Fee"],
        rows: [
          ["Up to ₹10 lakh", "1% of contravention"],
          ["₹10 lakh – ₹40 lakh", "1% up to ₹10L + 2% above"],
          ["₹40 lakh – ₹100 lakh", "Progressive up to 3%"],
          ["Above ₹100 lakh", "Additional slabs up to 5%"],
        ],
      },
    ],
    checklist: [
      "Identify contravention and quantify amount involved.",
      "Rectify the contravention (report late FC-GPR, unwind investment) before applying.",
      "Prepare application with facts, sections violated, and rectification proof.",
      "Submit at appropriate RBI Regional Office with ₹5,000 DD.",
      "Attend personal hearing before Compounding Authority.",
      "Pay fee within 15 days of order — else order becomes void.",
      "Order copy filed with authorised dealer bank for closure.",
      "PMLA-linked contraventions — cannot be compounded.",
    ],
    forms: [
      "Application for Compounding (per Master Direction) with annexures",
    ],
    portals: [
      { label: "RBI — Compounding", url: "https://www.rbi.org.in" },
    ],
  },

  // ============== INTERNATIONAL TAX ==============
  "international-tax:DTAAs & Tax Treaties": {
    authority: "CBDT",
    hero: {
      title: "DTAAs & Tax Treaties",
      description:
        "India's treaty network, treaty benefits, key withholding rates and Multilateral Instrument impact.",
    },
    summary: {
      governing: "Section 90/90A, Income-tax Act, 1961 · OECD MLI ratified 2019",
      authority: "CBDT · Ministry of Finance",
      effective: "MLI in force for India from 1 October 2019",
      overview:
        "India has over 90 comprehensive DTAAs and several TIEAs. Taxpayers may claim treaty benefits under Section 90 (or 90A for specified associations) — beneficial provisions apply to the extent they are more favourable than domestic law. To claim benefits, non-residents must furnish Tax Residency Certificate (TRC) and Form 10F. Tie-breaker rules under Article 4 determine treaty residence for dual residents. The Multilateral Instrument (MLI) has modified 26 of India's treaties, introducing the Principal Purpose Test (PPT) and Simplified LOB to counter treaty abuse.",
    },
    tables: [
      {
        title: "Illustrative Withholding Rates under Key Treaties",
        columns: ["Country", "Interest", "Royalty / FTS", "Dividend"],
        rows: [
          ["USA", "15% / 10%", "15% / 10% (FTS)", "25% / 15%"],
          ["UK", "10% / 15%", "10% / 15%", "10% / 15%"],
          ["Singapore", "10%", "10%", "10% / 15%"],
          ["UAE", "12.5%", "10%", "10%"],
          ["Mauritius", "7.5%", "10%", "5% / 15% (limited)"],
          ["Netherlands", "10%", "10%", "10% / 15%"],
        ],
      },
    ],
    checklist: [
      "Obtain TRC + Form 10F for every treaty claim by non-resident.",
      "Determine treaty residence via tie-breaker (permanent home → COVI → habitual abode → national).",
      "Apply lower of domestic or treaty rate; grossing-up under Sec. 195A if applicable.",
      "Post-MLI: satisfy Principal Purpose Test (PPT) — genuine commercial rationale.",
      "GAAR (Sec. 95-102) applies notwithstanding treaty relief.",
      "Business connection / PE analysis under Article 5 before claiming exemption.",
      "Fee for Technical Services — check 'make available' clause (esp. US, UK).",
      "MAP / APA for cross-border tax disputes and pre-emptive certainty.",
    ],
    forms: [
      "Form 10F — Information required for treaty benefit",
      "Form 15CA / 15CB — Foreign remittance certificate",
      "Form 67 — Foreign tax credit",
      "TRC (Tax Residency Certificate) — from home country",
    ],
    portals: [
      { label: "Income Tax Department — Treaty text", url: "https://incometaxindia.gov.in" },
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "international-tax:POEM": {
    authority: "CBDT",
    hero: {
      title: "Place of Effective Management (POEM)",
      description:
        "Residence rule for foreign companies under Section 6(3) with CBDT Guidelines.",
    },
    summary: {
      governing: "Section 6(3), Income-tax Act · CBDT Circular 6/2017",
      authority: "CBDT · Assessing Officer / Collegium",
      effective: "AY 2017-18 onwards",
      overview:
        "POEM is the place where key management and commercial decisions necessary for the business of the entity as a whole are, in substance, made. A foreign company is treated as an Indian resident (globally taxable) if its POEM is in India. CBDT's 2017 circular introduced the 'Active Business Outside India' (ABOI) safe-harbour: if a foreign company has ABOI (passive income ≤ 50%, assets ≤ 50%, employees ≤ 50%, payroll ≤ 50% in India), POEM is presumed outside India. Otherwise, a two-step test is applied — (i) identify persons who take key management/commercial decisions, and (ii) determine where those decisions are actually made. POEM determination requires prior approval from a collegium of 3 senior officers.",
    },
    tables: [
      {
        title: "ABOI Safe-Harbour Thresholds",
        columns: ["Parameter", "Threshold"],
        rows: [
          ["Passive income", "≤ 50% of total income"],
          ["Assets in India", "≤ 50% of total assets"],
          ["Employees in India", "≤ 50% of headcount"],
          ["Payroll on Indian employees", "≤ 50% of total payroll"],
        ],
      },
    ],
    checklist: [
      "Test ABOI first — if fully satisfied, POEM presumed outside India.",
      "Maintain evidence of board meetings held outside India (minutes, agenda).",
      "Ensure key decision-makers are not routinely resident in India.",
      "Document delegation of authority carefully; local ratification alone is not POEM.",
      "Turnover threshold — Sec. 6(3) does not apply if company's turnover ≤ ₹50 cr.",
      "AO must obtain collegium approval before determining POEM in India.",
      "If POEM in India — global income taxable; Foreign Tax Credit available.",
      "Withholding & CBCR obligations trigger once treated as Indian resident.",
    ],
    forms: [
      "Assessment records — no specific form; documentation is discretionary",
      "Board minutes and residency evidence for AO's review",
    ],
    portals: [
      { label: "CBDT — Circular 6/2017", url: "https://incometaxindia.gov.in" },
    ],
  },

  "international-tax:Equalisation Levy": {
    authority: "CBDT",
    hero: {
      title: "Equalisation Levy",
      description:
        "6% levy on digital advertising; the 2020 expanded 2% e-commerce levy withdrawn from 01 Aug 2024.",
    },
    summary: {
      governing: "Chapter VIII, Finance Act, 2016 · Finance Act 2020 amendments",
      authority: "CBDT",
      effective: "01 Jun 2016 (6%); 01 Apr 2020 (2%, withdrawn 01 Aug 2024)",
      overview:
        "The 2016 Equalisation Levy of 6% applies to online advertising services provided by non-residents to Indian residents/PE customers, on aggregate consideration exceeding ₹1 lakh in a financial year. The service recipient withholds and remits the levy. The 2020 expanded 2% levy on e-commerce supply or services by non-resident operators to Indian users was withdrawn effective 01 August 2024. Return filing continues in Form 1 by 30 June following the FY, and interest at 1% p.m. applies for late payment.",
    },
    tables: [
      {
        title: "Levy Framework",
        columns: ["Levy", "Rate", "Applicable To"],
        rows: [
          ["EL (2016) — Advertising", "6%", "Non-resident receiving > ₹1L / FY"],
          ["EL (2020) — E-commerce", "2%", "Withdrawn w.e.f. 01 Aug 2024"],
        ],
      },
    ],
    checklist: [
      "Deduct 6% on advertising consideration paid to non-resident.",
      "Deposit by 7th of following month.",
      "Payments corresponding to advertising exempt from income-tax (Sec. 10(50)).",
      "File Form 1 by 30 June every year.",
      "Interest 1% p.m. on late payment; penalty ₹1,000/day.",
      "No PE necessary — pure gross-basis levy.",
      "2020 e-commerce levy fully withdrawn from 01 Aug 2024.",
      "Maintain records — invoice, contract, remittance proof for 6 years.",
    ],
    forms: [
      "Form 1 — Annual EL statement",
      "Challan ITNS-285 — EL deposit",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "international-tax:CbCR": {
    authority: "CBDT",
    hero: {
      title: "Country-by-Country Reporting",
      description:
        "Consolidated group revenue disclosure — Master File, Local File, CbCR under Sections 286 & 92D.",
    },
    summary: {
      governing: "Section 286 · Section 92D · Rules 10DA & 10DB",
      authority: "CBDT",
      effective: "FY 2016-17 onwards for CbCR; Master/Local File FY 2016-17",
      overview:
        "CbCR (BEPS Action 13) applies to Indian resident parent entities of MNE groups whose consolidated revenue exceeds ₹6,400 crore. The parent files Form 3CEAD (CbCR) within 12 months of the end of the reporting accounting year. Constituent entities intimate their reporting entity in Form 3CEAC. Master File (Form 3CEAA Part A/B) applies when consolidated revenue > ₹500 cr and international transactions > ₹50 cr / intangibles > ₹10 cr. Local File is Form 3CEB, filed with ITR by every entity with international/specified domestic transactions.",
    },
    tables: [
      {
        title: "Documentation Thresholds",
        columns: ["Document", "Trigger Threshold"],
        rows: [
          ["CbCR (Form 3CEAD)", "Consolidated revenue > ₹6,400 cr"],
          ["Master File (Form 3CEAA)", "Cons. revenue > ₹500 cr + IT > ₹50 cr / intangibles > ₹10 cr"],
          ["Local File (Form 3CEB)", "International transactions with AE — any value"],
          ["Form 3CEAC", "Intimation by constituent entity"],
        ],
      },
      {
        title: "Filing Timelines",
        columns: ["Form", "Due Date"],
        rows: [
          ["3CEB (Local File)", "31 October of AY"],
          ["3CEAA (Master File)", "30 November of AY"],
          ["3CEAB (Master File designated entity)", "30 days before Master File due"],
          ["3CEAC (Constituent entity intimation)", "2 months before CbCR due"],
          ["3CEAD (CbCR)", "12 months from reporting FY end"],
        ],
      },
    ],
    checklist: [
      "Identify group's ultimate parent and threshold applicability.",
      "Constituent entities file 3CEAC to intimate reporting entity (parent or surrogate).",
      "Master File must contain functional analysis, intangibles, financial activities.",
      "CbCR includes revenue, PBT, income tax paid & accrued, capital, retained earnings, employees, tangible assets — per jurisdiction.",
      "Non-filing penalty ₹5,000/day (up to ₹5 L) for CbCR; ₹5 L flat for Master File.",
      "Concordance table between local file & TP study should be prepared.",
      "Cross-check with 92D documentation (Form 3CEB annexures).",
      "Maintain documentation for 8 years.",
    ],
    forms: [
      "Form 3CEB — Report from CA (Section 92E)",
      "Form 3CEAA / 3CEAB — Master File",
      "Form 3CEAC — Constituent entity intimation",
      "Form 3CEAD — CbCR",
      "Form 3CEAE — Notification of designated entity",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "international-tax:BEPS Action Plans": {
    authority: "CBDT",
    hero: {
      title: "BEPS Action Plans — India Implementation",
      description:
        "OECD BEPS 15 action plans with India-specific status as of August 2025.",
    },
    summary: {
      governing: "OECD BEPS Project (2013-15) · Finance Acts 2016-2024",
      authority: "CBDT · OECD / G20 Inclusive Framework",
      effective: "Progressive implementation via annual Finance Acts",
      overview:
        "The 15-point OECD BEPS action plan tackles tax avoidance strategies exploiting gaps and mismatches in international tax rules. India, an active participant of the Inclusive Framework, has implemented most action items through Finance Acts, treaty renegotiations, and the MLI. Notably, Actions 5 (Harmful Tax Practices), 6 (Treaty Abuse), 13 (CbCR), and 14 (Dispute Resolution) are directly reflected in domestic law. Pillar Two (15% Global Minimum Tax) legislation is pending Indian ratification.",
    },
    tables: [
      {
        title: "BEPS Action Plans & India Status",
        columns: ["Action", "Topic", "India Status"],
        rows: [
          ["1", "Digital economy", "Equalisation Levy; Sig. Economic Presence (Sec 9(1)(i))"],
          ["2", "Hybrid mismatches", "Section 79 (loss carryforward) & 94B"],
          ["3", "CFC rules", "Not adopted"],
          ["4", "Interest deductibility", "Section 94B — 30% EBITDA cap"],
          ["5", "Harmful tax practices", "Patent Box regime (Sec 115BBF)"],
          ["6", "Treaty abuse", "MLI PPT + LOB in select treaties"],
          ["7", "Permanent Establishment", "Section 9 amendments; SEP rule"],
          ["8-10", "TP intangibles / risk", "Rules 10D revised; DEMPE approach"],
          ["11", "BEPS data", "TP disclosure via 3CEB / 3CEAA"],
          ["12", "Mandatory disclosure", "Not yet fully adopted"],
          ["13", "CbCR", "Section 286 in force"],
          ["14", "Dispute resolution", "MAP + APA + Rule 44G"],
          ["15", "MLI", "Ratified — 26 treaties modified"],
          ["Pillar One", "Reallocation of profits", "MLC signing pending"],
          ["Pillar Two", "15% Global Minimum Tax", "Consultative — not yet enacted"],
        ],
      },
    ],
    checklist: [
      "Assess Section 94B interest cap for cross-border interest payments.",
      "Track Significant Economic Presence — deemed business connection for digital businesses.",
      "MLI application — identify Covered Tax Agreements and PPT applicability.",
      "Update TP documentation for DEMPE analysis on intangibles.",
      "Prepare CbCR Master File / Local File within statutory limits.",
      "Monitor Pillar Two GloBE Rules — anticipate 15% minimum tax obligations.",
      "MAP for treaty disputes — reduce double tax risk.",
      "Continuous training on BEPS interpretation given rapid changes.",
    ],
    forms: [
      "3CEB / 3CEAA / 3CEAC / 3CEAD (CbCR ecosystem)",
      "Form 34D — MAP application",
    ],
    portals: [
      { label: "OECD BEPS", url: "https://www.oecd.org/tax/beps" },
      { label: "CBDT", url: "https://incometaxindia.gov.in" },
    ],
  },

  // ============== TRANSFER PRICING ==============
  "transfer-pricing:Arm's Length Price": {
    authority: "CBDT",
    hero: {
      title: "Arm's Length Price — Six Prescribed Methods",
      description:
        "CUP, RPM, CPM, TNMM, PSM and the residual Other Method under Rule 10B.",
    },
    summary: {
      governing: "Section 92C · Rule 10B & 10AB, Income-tax Rules 1962",
      authority: "CBDT · Transfer Pricing Officer (TPO)",
      effective: "AY 2002-03 for domestic TP; extended to specified domestic transactions",
      overview:
        "Every international transaction between associated enterprises (or SDT beyond ₹20 cr threshold) must be at arm's length. Section 92C prescribes six methods for determining ALP — CUP (Comparable Uncontrolled Price), RPM (Resale Price Method), CPM (Cost Plus Method), TNMM (Transactional Net Margin Method), PSM (Profit Split Method), and the Other Method. The Most Appropriate Method (MAM) is selected based on nature of transaction, availability of data, and comparability. Multi-year data can be used for TNMM/PSM; single-year for CUP/RPM/CPM. The range concept (35th-65th percentile) applies if 6 or more comparables are available.",
    },
    tables: [
      {
        title: "TP Methods — When to Use",
        columns: ["Method", "Best Suited For"],
        rows: [
          ["CUP", "Commodities, royalties, benchmarked services with market price"],
          ["RPM", "Distributors adding no value"],
          ["CPM", "Contract manufacturers / service providers"],
          ["TNMM", "Most common — where reliable third-party gross margins unavailable"],
          ["PSM", "Highly integrated operations with unique intangibles"],
          ["Other Method", "Where none of the above applicable — Rule 10AB"],
        ],
      },
    ],
    checklist: [
      "Identify associated enterprises (Sec. 92A) — 26% shareholding or effective control.",
      "Classify transactions by type — services, goods, intangibles, financing.",
      "Perform FAR analysis to identify tested party and MAM.",
      "Search comparables from Indian databases (Prowess, Capitaline).",
      "Apply diagnostics — losses, unrelated segments, insufficient data — to eliminate outliers.",
      "Use multiple-year data judiciously (TNMM/PSM); single year data for CUP/RPM/CPM.",
      "Apply range if ≥ 6 comparables (35th-65th percentile); mean/median otherwise.",
      "Document rationale for method choice — audit tribunals scrutinise this heavily.",
    ],
    forms: [
      "Form 3CEB — CA certification of international / SDT",
      "TP study — statutory documentation (Rule 10D)",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "transfer-pricing:FAR Analysis": {
    authority: "CBDT",
    hero: {
      title: "FAR Analysis — Functions, Assets, Risks",
      description:
        "The core building block of transfer pricing that determines profit allocation.",
    },
    summary: {
      governing: "Rule 10B(2), Income-tax Rules 1962 · OECD TP Guidelines Chapter I",
      authority: "CBDT · TPO",
      effective: "Post BEPS Actions 8-10 — DEMPE approach",
      overview:
        "FAR (Functions performed, Assets employed, Risks assumed) analysis is the cornerstone of transfer pricing. It identifies the value chain, allocates functions between related parties, and determines which party bears economic risks. Post BEPS, mere contractual allocation is insufficient — the DEMPE (Development, Enhancement, Maintenance, Protection, Exploitation) approach for intangibles requires substance-based analysis. FAR feeds directly into selection of tested party, method choice, and comparables' search. Inadequate FAR is the most common cause of TP disputes in India.",
    },
    tables: [
      {
        title: "FAR Components",
        columns: ["Component", "Typical Examples"],
        rows: [
          ["Functions", "R&D, procurement, manufacturing, marketing, admin, sales"],
          ["Assets", "Tangible plant, working capital, intangibles (brand, tech, know-how)"],
          ["Risks", "Market, credit, inventory, FX, product liability, R&D failure"],
        ],
      },
    ],
    checklist: [
      "Prepare a value-chain map and identify each participant's role.",
      "Document who performs each function — with evidence (org chart, contracts, emails).",
      "Assets: separate routine vs unique intangibles; document DEMPE contributors.",
      "Risks: only entities with financial capacity + control over risk can bear it (BEPS).",
      "Tested party is usually the less complex, one-sided entity.",
      "Compare functional profile to comparables — reject those with material differences.",
      "Update FAR when business restructures — captive R&D → contract R&D shift, etc.",
      "Cross-reference FAR with Master File description of value creation.",
    ],
    forms: [
      "Chapter II & III of TP Study (Rule 10D documentation)",
    ],
    portals: [
      { label: "OECD TP Guidelines", url: "https://www.oecd.org/tax/transfer-pricing" },
    ],
  },

  "transfer-pricing:Advance Pricing Agreements": {
    authority: "CBDT",
    hero: {
      title: "Advance Pricing Agreements (APAs)",
      description:
        "Unilateral / Bilateral / Multilateral APAs — advance certainty on ALP up to 9 years.",
    },
    summary: {
      governing: "Section 92CC / 92CD · Rules 10F to 10T",
      authority: "CBDT · APA Team · Competent Authority (bilateral)",
      effective: "APA Program launched July 2012; Rollback provisions from AY 2014-15",
      overview:
        "APA is a formal agreement between the taxpayer and CBDT prescribing the methodology (and often the ALP itself) for determining the arm's length price of specified international transactions for future years — up to 5 forward + 4 rollback years. Three types: Unilateral (India only), Bilateral (India + treaty partner), and Multilateral (multiple jurisdictions). Filing fee: ₹10-20 lakh depending on transaction volume. Once signed, the APA is binding on the taxpayer and revenue and immunises the covered transactions from TP audit.",
    },
    tables: [
      {
        title: "APA Framework Highlights",
        columns: ["Feature", "Detail"],
        rows: [
          ["Types", "Unilateral / Bilateral / Multilateral"],
          ["Coverage", "5 forward + 4 rollback years"],
          ["Fee", "₹10L (< ₹100cr TX) / ₹15L / ₹20L (> ₹200cr)"],
          ["Timeline (typical)", "2-4 yrs unilateral; 3-6 yrs bilateral"],
          ["Renewal", "6 months before expiry"],
        ],
      },
    ],
    checklist: [
      "Assess if APA is right — recurring transactions with stable business model.",
      "Pre-filing consultation (voluntary but recommended) — Form 3CEC.",
      "File main APA application in Form 3CED with required fee.",
      "Furnish detailed FAR, financial data, benchmarking analysis.",
      "Participate in workshops with APA team; site visits may occur.",
      "Bilateral APA — signed by Competent Authorities of both countries after MAP-style negotiation.",
      "Compliance report annually in Form 3CEF confirming adherence.",
      "Rollback — modify past year assessments where APA methodology extended.",
    ],
    forms: [
      "Form 3CEC — Pre-filing consultation",
      "Form 3CED — Main application",
      "Form 3CEE — Withdrawal",
      "Form 3CEF — Annual Compliance Report",
    ],
    portals: [
      { label: "CBDT — APA", url: "https://incometaxindia.gov.in" },
    ],
  },

  "transfer-pricing:Safe Harbour Rules": {
    authority: "CBDT",
    hero: {
      title: "Safe Harbour Rules",
      description:
        "Prescribed margin ranges that, if adopted, exempt taxpayers from TP scrutiny.",
    },
    summary: {
      governing: "Section 92CB · Rule 10TA to 10TG",
      authority: "CBDT",
      effective: "Safe Harbour rules 2013; enhanced for FY 2019-20 to 2024-25",
      overview:
        "Safe Harbour Rules provide taxpayers an option to declare specified minimum margins on international transactions that, if adopted, are conclusively accepted as arm's length by the tax department. Eligible transactions include software development, IT-ES, KPO, contract R&D, generic pharma R&D, intra-group loans, corporate guarantees, and receipt of low-value-adding services. Option is exercised in Form 3CEFA annually for up to 5 consecutive years. Taxpayer may opt-out any year but foregoes safe-harbour immunity for that year.",
    },
    tables: [
      {
        title: "Safe Harbour Margins (Extended for FY 24-25)",
        columns: ["Transaction", "Threshold", "Safe Margin"],
        rows: [
          ["Software development", "TX ≤ ₹200 cr", "17-18%"],
          ["IT-Enabled Services", "TX ≤ ₹200 cr", "17-18%"],
          ["KPO", "Higher qualifications", "18-24%"],
          ["Contract R&D (SW)", "Any", "24%"],
          ["Contract R&D (generic pharma)", "Any", "24%"],
          ["Intra-group loan (INR)", "Any", "SBI 1-year MCLR + 425 bps"],
          ["Intra-group loan (FX)", "Any", "LIBOR/benchmark + 300 bps"],
          ["Corporate guarantee", "Any", "1% of amount guaranteed"],
          ["Low-value adding services", "Any", "5% mark-up"],
        ],
      },
    ],
    checklist: [
      "Confirm eligibility of transaction & threshold applicability.",
      "File Form 3CEFA before ITR due date to opt in.",
      "Maintain Rule 10D documentation even after opting-in.",
      "TPO cannot substitute margin unless safe-harbour is invalidated.",
      "Opt-out possible each year; not allowed mid-year.",
      "Not available for entities in Sec. 10AA (SEZ) with substantial losses.",
      "Cannot be combined with APA for the same transaction & year.",
      "Preserve TP study to substantiate that safe-harbour is adopted validly.",
    ],
    forms: [
      "Form 3CEFA — Opting-in application",
      "Form 3CEB — Annual CA report",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "transfer-pricing:TP Documentation": {
    authority: "CBDT",
    hero: {
      title: "Transfer Pricing Documentation",
      description:
        "Section 92D obligations, Master File, Local File and Form 3CEB.",
    },
    summary: {
      governing: "Section 92D · Rules 10D, 10DA, 10DB",
      authority: "CBDT",
      effective: "Master File / CbCR ecosystem from FY 2016-17",
      overview:
        "Every taxpayer with international transactions (or SDT above ₹20 cr) must maintain contemporaneous TP documentation under Rule 10D. This includes ownership structure, industry overview, FAR analysis, economic analysis, method selection, benchmarking, and comparability adjustments. Master File (Form 3CEAA) applies to MNE groups above thresholds. Form 3CEB — CA's audit report — must be filed by 31 October of the AY. Non-maintenance attracts 2% of transaction value under Section 271AA; non-filing of 3CEB attracts ₹1 lakh under 271BA.",
    },
    tables: [
      {
        title: "Documentation Hierarchy",
        columns: ["Layer", "Form / Rule", "Trigger"],
        rows: [
          ["Local File (Rule 10D)", "Kept internally", "IT with AE > ₹1 cr or SDT > ₹20 cr"],
          ["Form 3CEB", "Filed with ITR by 31 Oct AY", "Any international / SDT"],
          ["Master File (Rule 10DA)", "3CEAA by 30 Nov", "Group threshold > ₹500 cr + TX > ₹50 cr"],
          ["CbCR (Rule 10DB)", "3CEAD within 12 months", "Group revenue > ₹6,400 cr"],
        ],
      },
    ],
    checklist: [
      "Prepare TP study contemporaneously — before due date of ITR.",
      "Include 13-part documentation as per Rule 10D(1) & (2).",
      "Segregate international from domestic transactions in Form 3CEB.",
      "Include qualitative + quantitative analysis with comparables' PLI.",
      "Test method selection — document rationale to survive scrutiny.",
      "Reconcile with financial statements — reconciliation table mandatory.",
      "Retain documentation for 8 years from AY end.",
      "Update annually — refresh comparables, functional analysis, margins.",
    ],
    forms: [
      "Form 3CEB — Audit report on international transactions",
      "Form 3CEAA — Master File",
      "Form 3CEAB — Designated entity intimation",
      "Form 3CEAC / 3CEAD / 3CEAE — CbCR ecosystem",
    ],
    portals: [
      { label: "Income Tax e-Filing", url: "https://www.incometax.gov.in" },
    ],
  },

  "transfer-pricing:BEPS Transfer Pricing": {
    authority: "CBDT / OECD",
    hero: {
      title: "BEPS Transfer Pricing — Actions 8-10 & 13",
      description:
        "Aligning TP outcomes with value creation, and CbCR obligations.",
    },
    summary: {
      governing: "OECD TP Guidelines (2022) · Section 286 · Rule 10D revisions",
      authority: "CBDT · OECD",
      effective: "Progressive adoption via annual Finance Acts",
      overview:
        "BEPS Actions 8-10 rewrote Chapters I, II, and IV of the OECD TP Guidelines, focussing on aligning TP outcomes with actual value creation, addressing hard-to-value intangibles (HTVIs), and re-characterising transactions lacking commercial rationale. Action 13 introduced the three-tier documentation approach — Master File, Local File, CbCR — which India has fully implemented under Rule 10DA, 10DB and Section 286. India's TP rules also incorporate the DEMPE approach for intangibles, and the revised guidance on low-value-adding intra-group services aligns with the safe-harbour framework.",
    },
    tables: [
      {
        title: "BEPS Actions Relevant to TP",
        columns: ["Action", "Focus", "India Implementation"],
        rows: [
          ["8", "Intangibles — HTVI", "DEMPE analysis, safe harbours"],
          ["9", "Risk & Capital", "Substance-over-form; economic capacity"],
          ["10", "Other high-risk transactions", "Low-value services, cost contribution arrangements"],
          ["13", "Documentation & CbCR", "Rule 10DA / 10DB / Section 286"],
        ],
      },
    ],
    checklist: [
      "Document DEMPE contributions to any intangible.",
      "Ensure risk-bearing entity has decision-making capability + financial capacity.",
      "Reassess commercial rationality of intra-group financing and guarantees.",
      "Low-value-adding services: apply 5% mark-up per safe harbour.",
      "Maintain Master File and CbCR per thresholds.",
      "Revisit inter-company agreements post BEPS to reflect substance.",
      "Cash pooling and treasury arrangements — align returns with functions.",
      "Anticipate Pillar One / Two — assess if group is in scope.",
    ],
    forms: [
      "Form 3CEB · 3CEAA · 3CEAB · 3CEAC · 3CEAD",
    ],
    portals: [
      { label: "OECD BEPS", url: "https://www.oecd.org/tax/beps" },
      { label: "CBDT", url: "https://incometaxindia.gov.in" },
    ],
  },

  // ============== LABOUR LAWS ==============
  "labour-laws:PF & ESI": {
    authority: "EPFO / ESIC",
    hero: {
      title: "PF & ESI",
      description:
        "Contribution rates, applicability thresholds, ECR filing and penal provisions.",
    },
    summary: {
      governing: "EPF & MP Act 1952 · ESI Act 1948",
      authority: "EPFO (PF) · ESIC (ESI) · Ministry of Labour",
      effective: "PF applies from 20 employees; ESI wage ceiling ₹21,000 (₹25,000 disability)",
      overview:
        "The EPF Act applies to establishments employing 20 or more persons. Employees earning wages ≤ ₹15,000 are compulsorily covered; higher earners may opt in. Combined contribution is 24% of basic + DA (12% employer, 12% employee), of which 8.33% employer portion goes to Pension (EPS) capped at ₹1,250 per month. ESI applies to establishments with 10 or more employees; wage ceiling for coverage is ₹21,000 per month. Combined contribution is 4% (3.25% employer + 0.75% employee). Both contributions are deposited by 15th of the following month via unified ECR/challan.",
    },
    tables: [
      {
        title: "PF & ESI — Contribution Rates",
        columns: ["Scheme", "Employer", "Employee", "Wage Base"],
        rows: [
          ["EPF", "3.67% (12% – 8.33% to EPS)", "12%", "Basic + DA"],
          ["EPS (Pension)", "8.33% (capped ₹1,250)", "—", "Basic + DA"],
          ["EDLI (Insurance)", "0.50% (capped ₹75)", "—", "Wages"],
          ["Admin Charges", "0.50%", "—", "Wages"],
          ["ESI", "3.25%", "0.75%", "Gross wages"],
        ],
      },
    ],
    checklist: [
      "Register within 30 days of crossing employee threshold.",
      "Activate UAN & KYC for every employee (Aadhaar + PAN + bank).",
      "Deposit contributions by 15th of following month via ECR / ESI portal.",
      "Late deposit — interest 12% p.a. + damages 5-25% per Sec. 14B.",
      "Timely nomination in Form-2 for every employee.",
      "Submit ECR (Electronic Challan-cum-Return) by 15th.",
      "PF pensioners → EPS 95 rules; also higher-pension election window closed 03 Jul 2023.",
      "ESI benefits: medical, sickness, maternity, disablement, dependents.",
    ],
    forms: [
      "PF: Form 5A, Form 2 (nomination), Form 11 (declaration), Form 10D (pension)",
      "ESI: Form 01 (employer), Form 01A (registration), Form 3 (family details)",
      "ECR (Electronic Challan-cum-Return)",
    ],
    portals: [
      { label: "EPFO Unified Portal", url: "https://unifiedportal-emp.epfindia.gov.in" },
      { label: "ESIC Portal", url: "https://www.esic.gov.in" },
    ],
  },

  "labour-laws:Gratuity": {
    authority: "Ministry of Labour",
    hero: {
      title: "Payment of Gratuity Act, 1972",
      description:
        "Eligibility, calculation formula, maximum limit and taxation.",
    },
    summary: {
      governing: "Payment of Gratuity Act, 1972 · Rules 1972",
      authority: "Controlling Authority (State Govt)",
      effective: "Enacted 21 Aug 1972; maximum limit ₹20 lakh (Notification 29 Mar 2018)",
      overview:
        "Gratuity is a lump-sum retirement benefit payable to employees who have rendered continuous service of 5 years or more. It becomes payable on superannuation, retirement, resignation, or death/disablement (5-year condition waived on death/disability). The formula is 15 × last drawn wages × completed years of service ÷ 26 (for daily-rated employees, denominator differs). Maximum limit is ₹20 lakh (may be higher under employment contract). Government employees enjoy full exemption; private-sector employees enjoy exemption up to ₹20 lakh under Section 10(10) of Income-tax Act.",
    },
    tables: [
      {
        title: "Gratuity Formula & Tax Treatment",
        columns: ["Aspect", "Detail"],
        rows: [
          ["Eligibility", "5 years continuous service"],
          ["Formula", "15 × last wages × completed years / 26"],
          ["Maximum limit", "₹20 lakh (statutory)"],
          ["Exemption u/s 10(10)", "Actual OR statutory OR ₹20 L — whichever least"],
          ["Payment window", "30 days from due date"],
          ["Late payment interest", "Simple interest at notified rate"],
        ],
      },
    ],
    checklist: [
      "Establishments with 10+ employees are covered.",
      "5-year condition waived on death/disability.",
      "Employer must obtain gratuity insurance (LIC / notified insurer) unless exempted.",
      "Apply for gratuity via Form I within 30 days of due date.",
      "Employer to determine and pay within 30 days; interest thereafter.",
      "Any dispute — filed with Controlling Authority in Form N.",
      "Nomination in Form F is critical to reduce succession disputes.",
      "Gratuity forfeiture only in defined cases (fraud, riot, misconduct).",
    ],
    forms: [
      "Form A / B / C — Employer notifications",
      "Form F — Nomination",
      "Form I — Application by employee",
      "Form L — Payment order",
      "Form N — Application to Controlling Authority",
    ],
    portals: [
      { label: "Ministry of Labour", url: "https://labour.gov.in" },
    ],
  },

  "labour-laws:Minimum Wages": {
    authority: "Ministry of Labour",
    hero: {
      title: "Minimum Wages Act, 1948",
      description:
        "Scheduled employments, state-wise notification, VDA revisions and penalties.",
    },
    summary: {
      governing: "Minimum Wages Act, 1948 · Central and State schedules",
      authority: "Central & State Governments",
      effective: "Ongoing; consolidated into Code on Wages 2019 (partially notified)",
      overview:
        "The Minimum Wages Act 1948 empowers Central and State Governments to notify minimum rates of wages for scheduled employments — construction, agriculture, mining, industries, security, and hospitality. Wages are typically classified by skill (unskilled / semi-skilled / skilled / highly-skilled) and zone (urban/rural/mixed). Variable Dearness Allowance (VDA) is revised periodically (usually half-yearly) linked to CPI. Contractors and principal employers are jointly liable to ensure minimum-wage payment. The Code on Wages 2019, when fully notified, will replace this Act with a floor-wage concept.",
    },
    tables: [
      {
        title: "Enforcement Highlights",
        columns: ["Item", "Detail"],
        rows: [
          ["Wage revision", "Half-yearly VDA typically"],
          ["Skill categories", "Unskilled / Semi-skilled / Skilled / Highly-skilled"],
          ["Overtime", "Twice ordinary wage rate"],
          ["Payment cycle", "Weekly / Fortnightly / Monthly (< 1,000 workers within 7 days; else 10 days)"],
          ["Penalty", "Up to ₹500 fine + prosecution; higher under new Code"],
        ],
      },
    ],
    checklist: [
      "Track applicable schedule for each business location.",
      "Apply VDA revisions promptly on notification.",
      "Maintain wage-register (Form V/VI) per state rules.",
      "Ensure contractor labour also paid ≥ minimum wages.",
      "Overtime wages @ twice normal rate for work beyond 9 hrs/48 hrs.",
      "Deductions capped — house rent, damages, statutory only.",
      "Display abstract of Act (Form XII) at workplace.",
      "Prepare for Code on Wages transition — 4 codes ready for phased implementation.",
    ],
    forms: [
      "Form V — Register of wages",
      "Form VI — Wage slip",
      "Form XII — Abstract of the Act (display)",
    ],
    portals: [
      { label: "Ministry of Labour", url: "https://labour.gov.in" },
      { label: "Shram Suvidha", url: "https://shramsuvidha.gov.in" },
    ],
  },

  "labour-laws:Contract Labour Act": {
    authority: "Ministry of Labour",
    hero: {
      title: "Contract Labour (R&A) Act, 1970",
      description:
        "Applicability thresholds, principal employer & contractor obligations, welfare and licensing.",
    },
    summary: {
      governing: "Contract Labour (Regulation & Abolition) Act, 1970",
      authority: "Central / State Government · Chief Labour Commissioner",
      effective: "05 Feb 1971; state-specific rules and thresholds",
      overview:
        "The Act applies to every establishment or contractor employing 20 or more contract workers (50 in some states after amendments). Principal employer must obtain registration; contractor must obtain licence. The Act regulates working conditions, welfare (canteen, restroom, first-aid, drinking water), payment of wages, and prohibits contract labour in certain 'core' processes notified by government. The Occupational Safety, Health and Working Conditions Code 2020 will subsume this Act on full notification.",
    },
    tables: [
      {
        title: "Applicability & Licensing",
        columns: ["Aspect", "Detail"],
        rows: [
          ["Principal Employer threshold", "20 contract workers (50 in Karnataka, MP, etc.)"],
          ["Contractor licence", "Mandatory before deploying"],
          ["Licence validity", "Typically 12 months (renewable)"],
          ["Wage payment", "Wages disbursed in presence of authorised representative"],
          ["Welfare facilities", "Canteen (100+ workers), rest room, drinking water, first-aid"],
        ],
      },
    ],
    checklist: [
      "Obtain PE Registration in Form I; contractor obtains licence in Form IV.",
      "Maintain register of contract workers (Form XIII) and muster roll.",
      "Ensure PF, ESI, gratuity liability primary on contractor; principal residual.",
      "Welfare facilities depend on strength — canteen at 100+, restrooms otherwise.",
      "Timely payment of wages — before 7th (< 1,000 employees) or 10th of month.",
      "Notice of commencement / completion of contract to inspecting authority.",
      "Contract labour prohibited in specific processes (varies by state notification).",
      "Prepare for OSH Code 2020 — will replace and enhance obligations.",
    ],
    forms: [
      "Form I — Principal employer registration",
      "Form IV — Contractor licence",
      "Form V — Certificate by PE for licensing",
      "Form XIII — Register of contract workers",
    ],
    portals: [
      { label: "Shram Suvidha", url: "https://shramsuvidha.gov.in" },
      { label: "Ministry of Labour", url: "https://labour.gov.in" },
    ],
  },

  "labour-laws:Shops & Establishments": {
    authority: "State Government",
    hero: {
      title: "Shops & Establishments Acts",
      description:
        "State-wise applicability, registration, working hours and leave entitlements.",
    },
    summary: {
      governing: "State-specific Shops and Establishments Acts",
      authority: "State Labour Departments",
      effective: "State-specific — Delhi 1954, Maharashtra 2017, Karnataka 1961 etc.",
      overview:
        "Every commercial establishment (shop, office, restaurant, hotel, entertainment) must register under the applicable State Shops and Establishments Act within 30-60 days of commencement. The Act regulates working hours (typically 9 hrs/day, 48 hrs/week), rest intervals, weekly holiday, overtime, leave (usually 15 EL + 12 SL + 10 CL), payment of wages, employment of women and young persons, and closure days. Many states have moved to unified single-window portals for registration and renewal.",
    },
    tables: [
      {
        title: "Common Provisions (illustrative)",
        columns: ["Provision", "Typical Norm"],
        rows: [
          ["Working hours", "9 hrs/day; 48 hrs/week"],
          ["Overtime", "Twice the ordinary rate"],
          ["Weekly holiday", "One paid day per week"],
          ["Earned leave", "1 day for every 20 worked (~15 days)"],
          ["Sick / Casual leave", "10-12 days combined"],
          ["Night shift (women)", "Permitted with consent & security"],
        ],
      },
    ],
    checklist: [
      "Register within 30/60 days of starting business (state-specific).",
      "Display registration certificate at premises.",
      "Maintain attendance & wage registers per state rules.",
      "Compliance with leave / holidays / weekly-off / overtime.",
      "Female employment: permission if before 6am or after 8:30pm (state-specific).",
      "Renewal typically annually / triennially — track expiry.",
      "Closure notice — 15 days advance intimation to Inspector.",
      "State amendments — Maharashtra 2017 exempted small shops (< 10 workers) from many obligations.",
    ],
    forms: [
      "State-specific registration forms (Form A / Form F / SHOPS-1 etc.)",
      "Leave register / Attendance register / Wage register",
    ],
    portals: [
      { label: "Shram Suvidha", url: "https://shramsuvidha.gov.in" },
      { label: "State Labour Departments", url: "https://labour.gov.in" },
    ],
  },

  "labour-laws:Maternity Benefit": {
    authority: "Ministry of Labour",
    hero: {
      title: "Maternity Benefit Act, 1961",
      description:
        "26-week paid leave, creche facility, work from home and dismissal protection.",
    },
    summary: {
      governing: "Maternity Benefit Act 1961 (amended 2017)",
      authority: "Central Government · State Labour Commissioners",
      effective: "2017 amendment enhanced leave to 26 weeks (12 for 3rd child)",
      overview:
        "The Maternity Benefit Act 1961 (amended by the 2017 Amendment) applies to every establishment employing 10 or more persons. Women employees having worked at least 80 days in the preceding 12 months are entitled to 26 weeks of paid maternity leave (12 weeks for third or subsequent child). The 2017 amendment introduced a 12-week benefit for commissioning and adopting mothers, mandated crèche facilities for establishments with 50+ employees, and permits work-from-home post-leave by mutual agreement. Dismissal or disadvantageous alteration of service conditions during maternity is prohibited.",
    },
    tables: [
      {
        title: "Key Provisions",
        columns: ["Provision", "Detail"],
        rows: [
          ["Paid leave (1st & 2nd child)", "26 weeks (max 8 weeks before delivery)"],
          ["Paid leave (3rd child onward)", "12 weeks"],
          ["Adopting / Commissioning mother", "12 weeks"],
          ["Miscarriage / MTP", "6 weeks"],
          ["Nursing breaks", "Two prescribed intervals up to 15 months"],
          ["Crèche facility", "50+ employees; within prescribed distance"],
          ["Work from home", "Permitted post maternity leave by agreement"],
        ],
      },
    ],
    checklist: [
      "Employers must inform every female employee of benefits in writing at joining.",
      "Notice of intention by employee via Form K.",
      "Cash benefit paid at rate of average daily wage for absence period.",
      "No arduous work / long-standing for 1 month before expected delivery.",
      "Discharge / dismissal during maternity absence is void.",
      "Crèche must have proper facilities — separate from workspaces, safe access.",
      "Work-from-home arrangement documented in policy.",
      "Aligns with ESI benefits — ESI-covered women draw benefit from ESIC.",
    ],
    forms: [
      "Form K / L / M — Notices and claims",
      "Form O — Notice of birth",
    ],
    portals: [
      { label: "Ministry of Labour", url: "https://labour.gov.in" },
      { label: "ESIC (for insured women)", url: "https://www.esic.gov.in" },
    ],
  },
};

// Provide a graceful fallback for any subitem the sidebar renders that
// hasn't been populated yet (shouldn't be any — we cover all 40 pages).
export const getPage = (categoryKey, subitem) =>
  CONTENT_PAGES[pageKey(categoryKey, subitem)] || null;
